/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { formatters } from '@hcecbp/i18n-utils';
import { IDataError } from '@forge/data-connector';
import { Chart } from '@scuf/charts';
import IEnergyManagementProps, {
  IArraySeries,
  IBasicMonthWise,
  IBreakdownByMeterProps,
  IConvertDataForUtilityTrendChart,
  IEmissionsByUtility,
  IMonthWise,
  IPerformanceAggregationProps,
  IPortfolioUtilityCost,
  IServiceProvider,
  IUtilityCost,
  AreaWiseAggregations,
  IAny,
  IEuiResponse,
  IObject,
  ECartAt,
  EnergyDisaggregation,
  EnergyUsageAggregation,
  IRequestContext,
  IMonthWiseBaseObject
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import {
  ISavingsMeterProps,
  IMeterSavings,
  IUtilityMeterSavings,
  CarbonUtilityType,
  CarbonUtilityTypeScope,
  IUtilityCostSavings
} from '../SavingsMeter/SavingsMeterProps';
import { Badge, Icon } from '@scuf/common';
import { IOption } from '@scuf/common/dist/components/Select/ISelectProps';
import {
  AllUtilityDetails,
  breakdownUtilityProps,
  IUtilityProps
} from '../BreakdownUtility/BreakdownUtilityProps';
import {
  IAllConnectDetails,
  IUtilityProviders
} from '../../UtilityServiceModal/IUtilityService';
import React from 'react';
import AccountError from '../../../images/AccountError.svg';
import AccountWarning from '../../../images/AccountWarning.svg';
import AccountSuccess from '../../../images/AccountSuccess.svg';
import {
  IDSStatus,
  IUpdateAccount,
  IUtilityInfo,
  UtilityAccountStatus
} from '../../UtilityServiceSettings/IUtilityServiceSettingsProps';
import descriptors from '../../../locales/en-US/descriptors';
import { IMessageDescriptor, TFunction } from '@scuf/localization/dist/types';
import { IMeterDataProps } from '../BreakdownByMeter/BreakdownByMeterProps';
import {
  IUtilityAllMeterDetailData,
  UtilityMeterData
} from '../../DetailKPIWidgets/UtilityBillViewDetailsComponent/IUtilityBillViewDetails';
import moment from 'moment';
import {
  UtilityType,
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  MAGIC_VALUE,
  UtilityMessageMapping,
  UtilityStatusMessageKey,
  apiBadRequestStatusCode,
  apiConflictStatusCode,
  apiNoContentStatusCode,
  specialChars,
  specialCharsAutomaticAcc,
  FuelColorMappingFor6,
  FuelColorMapping,
  IntervalType,
  PERCENT_CHANGE_THRESHOLD,
  PlugMessageMappingWithDetail
} from './Constants';
import GasIcon from '../../../images/GasIcon.svg';
import ElectricityIcon from '../../../images/Energy-icon.svg';
import WaterIcon from '../../../images/WaterIcon.svg';
import SewerageIcon from '../../../images/sewerage_Icon.svg';
import {
  getCarbonTotalEmissionValues,
  getEnergyTotalUsagesValues
} from './unitHandling/EmaasSiteKpiDataScaling';
import { v4 as uuid4 } from 'uuid';
import {
  getConvertedValue,
  getMaxConvertedValueFromArray
} from './unitHandling/unitHandling';
import { ConversionType } from './unitHandling/UnitModel';

import TruncateText from '../TruncatedText/TruncateText';
import { EnergyUsageField } from '../Enums';
import { IArraySeriesProps } from '../../DashboardKPIWidgets/EnergyOptimization/EnergyOptimizationInterfaces';
import { config } from '../../DashboardKPIWidgets/EnergyOptimization/constants';
import { ISiteEuiData } from '../../DashboardKPIWidgets/EMaaSMobileSupervisorPortfolioDashboard/IPortfolioProps';
import { IAnyObject } from '../PerformanceGraph/IPerformanceGraph';
import {
  baselineFields,
  consuptionFields,
  IPortfolioEnergySite
} from '../../DetailKPIWidgets/PortfolioEnergyUsageViewDetails/PremiumSubscription/IPortfolioEnergyViewDetails';

import PapaParse from 'papaparse';
import { IStackedColumnProps } from '../StackedColumnChart/StackedColumnChart';
import { IFuelType } from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { getColorIndex } from './CarbonViewDetailsUtils';
import { endOfMonth, isSameDay, isSameMonth } from 'date-fns';
import { fuelGasTypeLabelCheck } from '../../ManualUpload/AddFuelModal/AddFuelUtils';
import {
  getupdatedSiteIDWithDetails,
  GuidEmpty
} from '../../ManualUpload/Scope1Table/Scope1TableUtils';
import _ from 'lodash';

export const returnFormattedDate = (dateToFormate: string) => {
  const dateTimeArray = dateToFormate.split('T');
  return dateTimeArray[0];
};

export const formattedDate = (dateToFormate: string) => {
  return moment(dateToFormate).format('YYYY-MM-DD');
};
export const formattedAddOneMoreDate = (dateToFormate: string) => {
  return moment(dateToFormate)
    .add(1, 'days')
    .format('YYYY-MM-DD');
};
export const formattedYYYYMMDate = (dateToFormate: string) => {
  return moment(dateToFormate).format('YYYY-MM');
};

export const formatEndDate = (dateToFormat: string) => {
  const dateTimeArray = dateToFormat.split(' ');
  return dateTimeArray[0];
};

export const isObjectWithNonNullValue = (object: Object): boolean => {
  return Object.values(object).some((value) => value !== null);
};

export const isObjectEmpty = (object: Object): boolean => {
  return Object.keys(object).length === 0 && object.constructor === Object;
};

export const isObjectOrArrayAndEmpty = (object: IAnyObject): boolean => {
  return isObjectEmpty(object) || (Array.isArray(object) && object.length == 0);
};

const mlist = [
  '',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec'
];

const getMListLocale = (index: number, lng: string = 'en-US') => {
  try {
    if (!lng) return mlist[index];
    const _monthArr = [''];
    const _localeList = moment()
      .locale(lng)
      .localeData()
      .monthsShort();
    _localeList.forEach((month) => {
      _monthArr.push(month);
    });
    return _monthArr[index];
  } catch (error) {
    return mlist[index];
  }
};

export const graphValue = (
  monthly:
    | IMonthWise[]
    | IConvertDataForUtilityTrendChart[]
    | IBasicMonthWise[]
    | IMonthWiseBaseObject[],
  lng?: string
) => {
  let monthIndex: Array<string> = [];
  let dayIndex: Array<string> = [];
  let sdayIndex: Array<string> = [];
  let yearIndex: Array<string> = [];
  let startyearIndex: Array<string> = [];
  let xAxisIndex: Array<string> = [];
  monthly.forEach((x) => {
    // for end date
    let b = x.monthEndDate.split('-');
    let year = b.splice(0, 1);
    let day = b.splice(b.length - 1, 1);
    dayIndex.push(day[0]);
    yearIndex.push(year.join());
    let f = b.shift()!;
    let c = f.replace(/^0+/, '');
    let fi = getMListLocale((c as unknown) as number, lng);
    monthIndex.push(fi);
    // for start date
    let a = x.monthStartDate.split('-');
    let yearStart = a.splice(0, 1);
    startyearIndex.push(yearStart.join());
    let sf = a.shift()!;
    let sm = sf.replace(/^0+/, '');
    let sday = a.splice(a.length - 1, 1);
    sdayIndex.push(sday[0]);
    let sfi = getMListLocale((sm as unknown) as number, lng);
    xAxisIndex.push(`${sday} ${sfi},${yearStart}-${day} ${fi},${year}`);
  });
  return { monthIndex, yearIndex, xAxisIndex };
};

export const graphValueNILM = (
  monthly: EnergyDisaggregation[],
  duration?: string,
  lng?: string
) => {
  let monthIndex: Array<string> = [];
  let yearIndex: Array<string> = [];
  monthly.forEach((x) => {
    let b = formattedDate(x.startDateTime).split('-');
    let year = b.splice(0, 1);
    yearIndex.push(year.join());
    let f = b.shift()!;
    let c = f.replace(/^0+/, '');
    let fi = getMListLocale((c as unknown) as number, lng);
    if (duration === IntervalType.Monthly) {
      monthIndex.push(`${fi} ${year[0].toString().slice(MAGIC_VALUE.TWO)}`);
    } else if (duration === IntervalType.Daily) {
      monthIndex.push(`${b} ${fi}`);
    } else {
      monthIndex.push(`${b} ${fi}`);
    }
  });
  return { monthIndex, yearIndex };
};

export const timeFormat = (hour: string) =>
  moment(hour, ['HH:mm']).format('hh:mm a');

export const graphValueNILMHourly = (
  monthly: EnergyDisaggregation[],
  requestContext: IRequestContext
) => {
  let monthIndex: Array<string> = [];
  let yearIndex: Array<string> = [];
  monthly.forEach((x) => {
    let b =
      x.startDateTime.split('T')[1] === undefined
        ? x.startDateTime
        : x.startDateTime
            .split('T')
            [MAGIC_VALUE.ONE].slice(MAGIC_VALUE.ZERO, MAGIC_VALUE.FIVE);
    if (requestContext.context.timeFormat === 'HH:mm') {
      monthIndex.push(`${b}`);
    } else {
      monthIndex.push(`${timeFormat(b)}`);
    }
  });
  return { monthIndex, yearIndex };
};

export const checkStartEndDateMonth = (
  checkStartEndDateMonth: EnergyDisaggregation[],
  requestContext: IAny
) => {
  let startDay = moment(formattedYYYYMMDate(requestContext.context.startTime));
  let endDay = moment(formattedYYYYMMDate(requestContext.context.endTime));
  let allMonthsInPeriod = [];

  while (startDay.isSameOrBefore(endDay)) {
    allMonthsInPeriod.push(startDay.format());
    startDay = startDay.add(1, 'month');
  }

  let checkingEmptyMonthsData: IAny[] = [];

  allMonthsInPeriod.forEach((value) => {
    let b = moment(value).format('YYYY-MM');
    let realData = checkStartEndDateMonth.filter(
      (v) => moment(v.startDateTime).format('YYYY-MM') === b
    );
    if (realData.length) {
      checkingEmptyMonthsData.push(...realData);
    } else {
      let emptyValuObject = {
        disaggregation: [],
        startDateTime: value,
        endDateTime: value,
        totalConsumption: null,
        totalBaselineConsumption: null,
        isCompleted: false,
        consumptionProcessedDays: 0
      };
      checkingEmptyMonthsData.push(emptyValuObject);
    }
  });
  return checkingEmptyMonthsData;
};

export const checkStartEndDateDays = (
  checkStartEndDateMonth: EnergyDisaggregation[],
  requestContext: IAny
) => {
  let startDay = moment(formattedDate(requestContext.context.startTime));
  let endDay = moment(formattedDate(requestContext.context.endTime));
  let allMonthsInPeriod = [];
  while (startDay.isSameOrBefore(endDay)) {
    allMonthsInPeriod.push(startDay.format());
    startDay = startDay.add(1, 'day');
  }

  let checkingEmptyMonthsData: IAny[] = [];
  allMonthsInPeriod.forEach((value) => {
    let b = formattedDate(value);
    let realData = checkStartEndDateMonth.filter(
      (v) => formattedDate(v.startDateTime) === b
    );
    if (realData.length) {
      checkingEmptyMonthsData.push(...realData);
    } else {
      let emptyValuObject = {
        disaggregation: [],
        startDateTime: value,
        endDateTime: value,
        totalConsumption: null,
        totalBaselineConsumption: null,
        isCompleted: false,
        consumptionProcessedDays: 0
      };
      checkingEmptyMonthsData.push(emptyValuObject);
    }
  });
  return checkingEmptyMonthsData;
};

export const checkStartEndDateHourly = (
  checkStartEndDateMonth: EnergyDisaggregation[],
  requestContext: IAny
) => {
  const startOfDay = moment().startOf('day');
  const hours = Array(MAGIC_VALUE.TWENTYFOUR)
    .fill(null)
    .map((_, i) =>
      startOfDay
        .add(i ? MAGIC_VALUE.SIXTY : MAGIC_VALUE.ZERO, 'minutes')
        .format('HH:mm')
    );
  let checkingEmptyMonthsData: IAny[] = [];
  hours.forEach((value) => {
    let realData = checkStartEndDateMonth.filter(
      (v) =>
        v.startDateTime
          .split('T')
          [MAGIC_VALUE.ONE].slice(MAGIC_VALUE.ZERO, MAGIC_VALUE.FIVE) === value
    );
    if (realData.length) {
      checkingEmptyMonthsData.push(...realData);
    } else {
      let emptyValuObject = {
        disaggregation: [],
        startDateTime: value,
        endDateTime: value,
        totalConsumption: null,
        totalBaselineConsumption: null,
        isCompleted: false,
        consumptionProcessedDays: 0
      };
      checkingEmptyMonthsData.push(emptyValuObject);
    }
  });
  return checkingEmptyMonthsData;
};

export const graphValueNILMTable = (
  monthly: EnergyDisaggregation[],
  interValType?: IAny,
  lng?: string
) => {
  let monthIndex: Array<string> = [];
  let yearIndex: Array<string> = [];
  monthly.forEach((x) => {
    if (interValType === IntervalType.Monthly) {
      let b = x.startDateTime.split('-');
      let year = b.splice(0, 1);
      yearIndex.push(year.join());
      let f = b.shift()!;
      let c = f.replace(/^0+/, '');
      let fi = getMListLocale((c as unknown) as number, lng);
      monthIndex.push(
        `${fi} ${year[MAGIC_VALUE.ZERO].toString().slice(MAGIC_VALUE.TWO)}`
      );
    } else if (interValType === IntervalType.Daily) {
      monthIndex.push(moment(x.startDateTime).format('DD/MM/YYYY'));
    } else {
      monthIndex.push(
        x.startDateTime.split('T')[1] === undefined
          ? x.startDateTime
          : x.startDateTime
              .split('T')
              [MAGIC_VALUE.ONE].slice(MAGIC_VALUE.ZERO, MAGIC_VALUE.FIVE)
      );
    }
  });
  return { monthIndex, yearIndex };
};

export const tableValueNILM = (
  energyDisaggregation: EnergyDisaggregation[],
  unit: IAny,
  scaledUnit: (value: number) => number | null,
  duration?: string,
  lng?: string
) => {
  let monthIndex: {
    month: string;
    totalConsumption: number | null;
    baselineConsumption: number | null;
    id: string;
    name: string;
    consumptionProcessedDays: number;
    unit: any;
  }[] = [];
  let yearIndex: Array<string> = [];
  energyDisaggregation?.forEach((x) => {
    let b = formattedDate(x.startDateTime).split('-');
    let year = b.splice(0, 1);
    yearIndex.push(year.join());
    let f = b.shift()!;
    let c = f.replace(/^0+/, '');
    let fi = getMListLocale((c as unknown) as number, lng);
    x?.disaggregation?.forEach((y) => {
      let data = {
        month:
          duration === IntervalType.Monthly
            ? `${fi} ${year[MAGIC_VALUE.ZERO]
                .toString()
                .slice(MAGIC_VALUE.TWO)}`
            : `${moment(x.startDateTime).format('DD/MM/YYYY')}`,
        totalConsumption: scaledUnit(y.totalConsumption!),
        baselineConsumption: scaledUnit(y.baselineConsumption!),
        id: y.id,
        name: y.name,
        consumptionProcessedDays: y.consumptionProcessedDays,
        unit: unit
      };
      monthIndex.push(data);
    });
    let firstData = {
      month:
        duration === IntervalType.Monthly
          ? `${fi} ${year[MAGIC_VALUE.ZERO].toString().slice(MAGIC_VALUE.TWO)}`
          : `${moment(x.startDateTime).format('DD/MM/YYYY')}`,
      totalConsumption: scaledUnit(x.totalConsumption!),
      baselineConsumption: scaledUnit(x.baselineConsumption!),
      id: uuid4(),
      name: 'All (Total)',
      consumptionProcessedDays: x.consumptionProcessedDays,
      unit: unit
    };
    monthIndex.unshift(firstData);
  });
  let uniqueNames = monthIndex.filter(
    (a, i) => monthIndex.findIndex((s) => a.name === s.name) === i
  );
  let disaggregationMap = uniqueNames.map((value, index) => {
    let data = {
      name: value?.name,
      id: value?.id,
      unit: value?.unit,
      color:
        index > 0
          ? uniqueNames.length <= MAGIC_VALUE.SIX
            ? FuelColorMappingFor6[index - MAGIC_VALUE.ONE]
            : FuelColorMapping[getColorIndex(index - MAGIC_VALUE.ONE)]
          : ''
    };
    monthIndex.forEach((newVal) => {
      if (value?.name === newVal.name) {
        data = {
          ...data,
          [newVal.month]: newVal?.totalConsumption
        };
      }
    });
    return data;
  });
  return { disaggregationMap, yearIndex };
};

export const tableValueNILMHourly = (
  energyDisaggregation: EnergyDisaggregation[],
  unit: IAny,
  scaledUnit: (value: number) => number | null,
  requestContext: IRequestContext
) => {
  let monthIndex: {
    month: string;
    totalConsumption: number | null;
    baselineConsumption: number | null;
    id: string;
    name: string;
    consumptionProcessedDays: number;
    unit: any;
  }[] = [];
  let yearIndex: Array<string> = [];
  energyDisaggregation?.forEach((x) => {
    let startTime =
      x.startDateTime.split('T')[1] === undefined
        ? x.startDateTime
        : x.startDateTime
            .split('T')
            [MAGIC_VALUE.ONE].slice(MAGIC_VALUE.ZERO, MAGIC_VALUE.FIVE);
    let TimeFormateChange: string;
    if (requestContext?.context?.timeFormat === 'HH:mm') {
      TimeFormateChange = startTime;
    } else {
      TimeFormateChange = timeFormat(startTime);
    }

    x?.disaggregation?.forEach((y) => {
      let data = {
        month: TimeFormateChange,
        totalConsumption: scaledUnit(y.totalConsumption!),
        baselineConsumption: scaledUnit(y.baselineConsumption!),
        id: y.id,
        name: y.name,
        consumptionProcessedDays: y.consumptionProcessedDays,
        unit: unit
      };
      monthIndex.push(data);
    });
    let firstData = {
      month: TimeFormateChange,
      totalConsumption: scaledUnit(x.totalConsumption!),
      baselineConsumption: scaledUnit(x.baselineConsumption!),
      id: uuid4(),
      name: 'All (Total)',
      consumptionProcessedDays: x.consumptionProcessedDays,
      unit: unit
    };
    monthIndex.unshift(firstData);
  });
  let uniqueNames = monthIndex.filter(
    (a, i) => monthIndex.findIndex((s) => a.name === s.name) === i
  );
  let disaggregationMap = uniqueNames.map((value, index) => {
    let data = {
      name: value?.name,
      id: value?.id,
      unit: value?.unit,
      color:
        index > 0
          ? uniqueNames.length <= 6
            ? FuelColorMappingFor6[index - 1]
            : FuelColorMapping[getColorIndex(index - 1)]
          : ''
    };
    monthIndex.forEach((newVal) => {
      if (value?.name === newVal.name) {
        data = {
          ...data,
          [newVal.month]: newVal?.totalConsumption
        };
      }
    });
    return data;
  });
  return { disaggregationMap, yearIndex };
};
export const graphCarbon = (
  monthWiseCorbonConsumption: Array<IMonthWise>,
  fromUnit: string,
  toUnit: string,
  totalSiteCount?: number,
  lng?: string
) => {
  let value = graphValue(monthWiseCorbonConsumption, lng);
  let YearIndex: Array<string> = [];
  let MonthIndex: Array<string> = [];
  let actual: Array<number | null> = [];
  let previousPeriod: Array<number | null> = [];
  let IsCompleted: Array<boolean> = [];
  let PeriodUpto: Array<number> = [];
  let noDataActual: boolean = false;
  let noDataPrevious: boolean = false;
  let SiteWithDataCount: any = [];

  monthWiseCorbonConsumption.forEach((x: IMonthWise) => {
    if (x.totalActualEmission == null || x.totalActualEmission == 0) {
      actual.push(0);
      noDataActual = true;
    } else {
      const unitScaledActual = getConvertedValue(
        ConversionType.CARBON,
        fromUnit,
        x.totalActualEmission,
        toUnit
      ).Value;
      actual.push(unitScaledActual);
    }
    if (
      x.totalPreviousPeriodEmission == null ||
      x.totalPreviousPeriodEmission == 0
    ) {
      previousPeriod.push(0);
      noDataPrevious = true;
    } else {
      const unitScaledPreviousPeriod = getConvertedValue(
        ConversionType.CARBON,
        fromUnit,
        x.totalPreviousPeriodEmission,
        toUnit
      ).Value;
      previousPeriod.push(unitScaledPreviousPeriod);
    }

    IsCompleted.push(x.isCompleted);
    // PeriodUpto.push(
    //   moment(new Date(x.monthEndDate)).diff(
    //     moment(new Date(x.monthStartDate)),
    //     'days'
    //   ) + 1
    // );
    PeriodUpto.push(x.emissionProcessedDays);
    YearIndex = value.yearIndex;
    MonthIndex = value.monthIndex;
    SiteWithDataCount.push(x.sitesWithData);
  });
  return {
    actual,
    previousPeriod,
    IsCompleted,
    YearIndex,
    MonthIndex,
    PeriodUpto,
    noDataActual,
    noDataPrevious,
    SiteWithDataCount,
    totalSiteCount
  };
};

export const graphEnergy = (
  monthWiseEnergyConsumption: IConvertDataForUtilityTrendChart[],
  totalSiteCount?: number,
  lng?: string
) => {
  let MonthIndex = [];
  let IsCompleted: Array<boolean> = [];
  let PeriodUpto: Array<number> = [];
  let YearIndex = [];
  let actual: Array<number> = [];
  let previousPeriod: Array<number> = [];
  let noDataActual: boolean = false;
  let noDataPrevious: boolean = false;
  let SiteWithDataCount: any = [];

  let value = graphValue(monthWiseEnergyConsumption, lng);
  MonthIndex = value.monthIndex;
  YearIndex = value.yearIndex;
  monthWiseEnergyConsumption.forEach((x) => {
    if (x.totalActualCost == null || x.totalActualCost == 0) {
      actual.push(0);
      noDataActual = true;
    } else actual.push(x.totalActualCost);
    if (x.previousPeriodCost == null || x.previousPeriodCost == 0) {
      previousPeriod.push(0);
      noDataPrevious = true;
    } else previousPeriod.push(x.previousPeriodCost);
    IsCompleted.push(x.isCompleted);
    PeriodUpto.push(x.consumptionProcessedDays);
    YearIndex = value.yearIndex;
    MonthIndex = value.monthIndex;
    SiteWithDataCount.push(x.sitesWithData);
  });
  return {
    actual,
    previousPeriod,
    YearIndex,
    MonthIndex,
    IsCompleted,
    PeriodUpto,
    noDataActual,
    noDataPrevious,
    SiteWithDataCount,
    totalSiteCount
  };
};

export const getAbsoluteValue = (parms: number) => Math.abs(parms);

const getChangeAbsValue = (
  absValue: IAny,
  symbol: IAny,
  formatedValue: IAny
) => {
  return absValue == 0
    ? formatters.number.currency(symbol || 'USD')(absValue)
    : formatTheDKKValue(formatedValue);
};

export const symbolObj: IAny = {
  CRC: '₡',
  SAR: 'ر.س',
  AED: 'د.إ',
  PLN: 'zł',
  MYR: 'RM',
  RM: 'RM',
  SGD: '$',
  HUF: 'Ft',
  USD: '$',
  A$: '$',
  CA$: '$',
  'CN¥': '¥',
  MX$: '$',
  MEX$: '$',
  CAD: '$',
  C$: '$',
  MXN: '$',
  AUD: '$',
  KWD: 'د.ك',
  BHD: 'د.ب',
  JOD: 'د.ا',
  EGP: 'E£',
  LBP: 'ل.ل',
  SDG: 'ج.س',
  OMR: 'ر.ع.',
  QAR: 'ر.ق'
};

const forINR = [
  {
    minValue: 0,
    maxValue: 999,
    displayUnit: '₹'
  },
  {
    minValue: 1000,
    maxValue: 99999,
    displayUnit: '₹',
    prefix: 'K'
  },
  {
    minValue: 100000,
    maxValue: 9999999,
    displayUnit: '₹',
    prefix: 'Lk'
  },
  {
    minValue: 10000000,
    maxValue: 999999999,
    displayUnit: '₹',
    prefix: 'Cr'
  }
];

const formatINR = (value: IAny) => {
  let displayValue = '';
  forINR.forEach((element) => {
    if (value >= element.minValue && value <= element.maxValue) {
      displayValue = `${element.displayUnit}${
        element.minValue === 0
          ? (value / 1).toFixed(2)
          : (value / element.minValue).toFixed(2)
      } ${element.prefix ? element.prefix : ''}`;
    }
  });
  return displayValue;
};

const currencyToExclude = ['KWD', 'MYR', 'MX$', 'MXN', 'OMR'];

export const formatCurrency = (
  value: number,
  symbol: string | undefined,
  absVal = true,
  toFixedValue?: number
) => {
  const absValue = absVal ? Math.abs(value) : value;
  let formatedValue;
  if (symbol === 'INR') {
    formatedValue = formatINR(absValue);
  } else {
    formatedValue = toFixedValue
      ? formatters.number.currency(symbol || 'USD')(
          absValue,
          toFixedValue,
          toFixedValue
        )
      : formatters.number.currency(symbol || 'USD')(absValue);
  }

  if (symbol === 'DKK') {
    // let symbolDKK = symbol === 'DKK' ? 'Kr.' : symbol;
    let valueChange = getChangeAbsValue(absValue, symbol, formatedValue);
    // As "Kr." is the currency symbol for Danish Krone. the lib which we are using will give "DKK" so in the bellow condition if the currency in DKK will change it to "kr."
    let removeSpace;
    if (valueChange.slice(0, 3) === 'DKK') {
      removeSpace = 'kr.' + valueChange.slice(4);
    } else {
      removeSpace = valueChange;
    }
    return removeSpace;
  } else {
    let valueChange = !currencyToExclude.includes(formatedValue.slice(0, 3))
      ? absValue <= 0
        ? formatters.number.currency(symbol || 'USD')(absValue)
        : formatTheValue(formatedValue)
      : formatedValue;
    let removeSpace;
    if (valueChange.slice(0, 3) === 'DKK') {
      removeSpace = 'kr.' + valueChange.slice(4);
    } else {
      removeSpace = valueChange;
    }

    if (
      [
        'CRC',
        'SAR',
        'AED',
        'PLN',
        'MYR',
        'HUF',
        'USD',
        'CN¥',
        'SGD',
        'CAD',
        'CA$',
        'MXN',
        'MX$',
        'KWD',
        'BHD',
        'JOD',
        'EGP',
        'LBP',
        'SDG',
        'OMR',
        'QAR'
      ].includes(removeSpace.slice(0, 3))
    ) {
      removeSpace = symbolObj[removeSpace.slice(0, 3)] + removeSpace.slice(3);
    } else if (['A$'].includes(removeSpace.slice(0, 2))) {
      removeSpace = symbolObj[removeSpace.slice(0, 2)] + removeSpace.slice(2);
    }
    if (removeSpace.slice(0, 1) === '-') {
      if (
        [
          'CRC',
          'SAR',
          'AED',
          'PLN',
          'MYR',
          'HUF',
          'USD',
          'CN¥',
          'SGD',
          'CAD',
          'CA$',
          'MXN',
          'MX$',
          'KWD',
          'BHD',
          'JOD',
          'EGP',
          'LBP',
          'SDG',
          'OMR',
          'QAR'
        ].includes(removeSpace.slice(1, 4))
      ) {
        removeSpace = `-${
          symbolObj[removeSpace.slice(1, 4)]
        } ${removeSpace.slice(4)}`;
      } else if (['A$'].includes(removeSpace.slice(1, 3))) {
        removeSpace = `-${
          symbolObj[removeSpace.slice(1, 3)]
        }  ${removeSpace.slice(3)}`;
      }
    }
    return removeSpace;
  }
};

export const formatTheDKKValue = (inputValue: string) => {
  let removeSpace;
  // As "Kr." is the currency symbol for Danish Krone. the lib which we are using will give "DKK" so in the bellow condition if the currency in DKK will change it to "kr."
  if (inputValue.slice(0, 3) === 'DKK') {
    removeSpace = 'kr.' + inputValue.slice(4);
  } else {
    //removeSpace = inputValue.slice(0, 3) + inputValue.slice(4);
    removeSpace = '-kr.' + inputValue.slice(4);
  }
  let splitValue = removeSpace.split('');
  let numberMatch = (inputValue.match(/[0-9]/g) || []).pop();
  let matchedIndex = splitValue.lastIndexOf(numberMatch!);
  if (matchedIndex < splitValue.length - 1) {
    splitValue.splice(splitValue.length - 1, 0, ' ');
    return splitValue.toString().replace(/,/g, '');
  }
  return removeSpace;
};

export const formatTheValue = (inputValue: string) => {
  let inputValueArray = inputValue.split('');
  let numberMatch = (inputValue.match(/[0-9]/g) || []).pop();
  let matchedIndex = inputValueArray.lastIndexOf(numberMatch!);
  if (matchedIndex < inputValueArray.length - 1) {
    let updatedDKKInput;
    // As "Kr." is the currency symbol for Danish Krone. the lib which we are using will give "DKK" so in the bellow condition if the currency in DKK will change it to "kr."
    if (inputValue.slice(0, 3) === 'DKK') {
      updatedDKKInput = `kr. ${inputValue.slice(4)}`;
    } else {
      updatedDKKInput = inputValue;
    }
    let newArray = updatedDKKInput.split(
      updatedDKKInput[inputValueArray.length - 1]
    );
    return newArray[0] + ' ' + updatedDKKInput[inputValueArray.length - 1];
  }
  // As "Kr." is the currency symbol for Danish Krone. the lib which we are using will give "DKK" so in the bellow condition if the currency in DKK will change it to "kr."
  let updatedDKKCheck;
  if (inputValue.slice(0, 3) === 'DKK') {
    updatedDKKCheck = `kr. ${inputValue.slice(4)}`;
  } else {
    updatedDKKCheck = inputValue;
  }
  return updatedDKKCheck;
};

export const formatPriceValue = (inputValue: string) => {
  let inputValueArray = inputValue.split('');
  let numberMatch = (inputValue.match(/[0-9]/g) || []).pop();
  let matchedIndex = inputValueArray.lastIndexOf(numberMatch!);
  if (matchedIndex < inputValueArray.length - 1) {
    let checkIfDKK = inputValueArray
      .slice(0, 3)
      .toString()
      .replace(/,/g, '');
    if (checkIfDKK === 'DKK') {
      let val = inputValueArray.slice(0, inputValueArray.length - 1);
      return val.toString().replace(/,/g, '');
    } else {
      let newArray = inputValue.split(inputValue[inputValueArray.length - 1]);
      return newArray[0] && newArray[0].trim();
    }
  }
  return inputValue;
};

export const formatUnit = (inputUnit: string) => {
  let inputUnitArray = inputUnit.split('');
  let numberMatch = (inputUnit.match(/[0-9]/g) || []).pop();
  let matchedIndex = inputUnitArray.lastIndexOf(numberMatch!);
  if (matchedIndex < inputUnitArray.length - 1) {
    return inputUnit[inputUnitArray.length - 1];
  }
  return '';
};

export const symbolObjForBuilder: IAny = {
  CRC: '₡',
  SAR: 'ر.س',
  AED: 'د.إ',
  PLN: 'zł',
  MYR: 'RM',
  RM: 'RM',
  SGD: '$',
  HUF: 'Ft',
  USD: '$',
  AUD: '$',
  CAD: '$',
  CNY: '¥',
  MX$: '$',
  MEX$: '$',
  MXN: '$',
  KWD: 'د.ك',
  BHD: 'د.ب',
  JOD: 'ينار',
  EGP: 'E£',
  LBP: 'ل.ل',
  SDG: 'ج.س',
  OMR: 'ر.ع.',
  QAR: 'ر.ق',
  DKK: 'kr.',
  'CN¥': '¥',
  A$: '$',
  CA$: '$'
};

export const getSymbolFromCurrency = (currencyCode: string) => {
  if (
    [
      'CRC',
      'SAR',
      'AED',
      'PLN',
      'MYR',
      'HUF',
      'USD',
      'AUD',
      'CAD',
      'CNY',
      'SGD',
      'MX$',
      'MEX$',
      'RM',
      'KWD',
      'BHD',
      'JOD',
      'EGP',
      'LBP',
      'SDG',
      'OMR',
      'QAR',
      'DKK',
      'MXN'
    ].includes(currencyCode)
  ) {
    return symbolObjForBuilder[currencyCode];
  }

  return formatters.number
    .currency(currencyCode || 'USD')(0)
    .split('0')[0];
};

export const convertDataForTrendChart = (
  monthwiseAggregations: Array<IConvertDataForUtilityTrendChart>,
  totalSiteCount?: any,
  lng?: string
) => {
  let value = graphValue(monthwiseAggregations, lng);

  let IsCompleted: Array<boolean> = [];
  let PeriodUpto: Array<number> = [];
  let baselineConsumptionData: Array<number> = [];
  let baselineCostData: Array<number> = [];
  let actualConsumptionData: Array<number> = [];
  let actualCostData: Array<number> = [];
  let YearIndex: Array<string> = [];
  let MonthIndex: Array<string> = [];
  YearIndex = value.yearIndex;
  MonthIndex = value.monthIndex;
  let SiteWithDataCount: any = [];

  monthwiseAggregations.forEach(
    ({
      totalActualConsumption,
      totalActualCost,
      previousPeriodConsumption,
      previousPeriodCost,
      isCompleted,
      consumptionProcessedDays,
      sitesWithData
    }) => {
      IsCompleted.push(isCompleted);
      PeriodUpto.push(consumptionProcessedDays);
      baselineConsumptionData.push(previousPeriodConsumption ?? 0);
      baselineCostData.push(previousPeriodCost ?? 0);
      actualConsumptionData.push(totalActualConsumption ?? 0);
      actualCostData.push(totalActualCost ?? 0);
      SiteWithDataCount.push(sitesWithData);
    }
  );
  return {
    baselineConsumptionData,
    baselineCostData,
    actualConsumptionData,
    actualCostData,
    MonthIndex,
    YearIndex,
    IsCompleted,
    PeriodUpto,
    totalSiteCount,
    SiteWithDataCount
  };
};

export const getStatusCodeMessage = (
  statusCode: number,
  error: IDataError,
  t: TFunction
) => {
  const d = descriptors.energyManagement;
  let message = '';
  if (statusCode === apiBadRequestStatusCode) {
    message = error?.rawResponse?.response?.data?.error[0]?.detail;
  } else if (statusCode === apiConflictStatusCode) {
    message = error?.rawResponse?.response?.data?.error?.detail;
  } else if (statusCode === apiNoContentStatusCode) {
    message = t(d.EOSETTINGNODATAAVAILABLE);
  } else {
    message = error?.rawResponse?.response?.data;
  }
  return t(message || error?.rawResponse?.message || error?.name);
};

export const getNotifyMessage = (isEOEnabled: boolean, t: TFunction) => {
  const d = descriptors.energyManagement;
  return isEOEnabled ? t(d.EOSTATUSMESSAGEERROR) : t(d.EOSTATUSMESSAGESUCCESS);
};
export const convertDataForUtilityTrendChart = (
  monthwiseAggregation: Array<IConvertDataForUtilityTrendChart>,
  fromUnit: string,
  toUnit: string,
  conversionType: string,
  lng?: string
) => {
  let value = graphValue(monthwiseAggregation, lng);
  let YearIndex: Array<string> = [];
  let MonthIndex: Array<string> = [];
  YearIndex = value.yearIndex;
  MonthIndex = value.monthIndex;

  let IsCompleted: Array<boolean> = [];
  let PeriodUpto: Array<number> = [];
  let baselineConsumptionData: Array<number> = [];
  let baselineCostData: Array<number> = [];
  let actualConsumptionData: Array<number> = [];
  let actualCostData: Array<number> = [];

  monthwiseAggregation.forEach(
    ({
      totalActualConsumption,
      totalActualCost,
      previousPeriodConsumption,
      previousPeriodCost,
      isCompleted,
      consumptionProcessedDays
    }) => {
      IsCompleted.push(isCompleted);
      PeriodUpto.push(consumptionProcessedDays);
      const unitScaledPrevious = getConvertedValue(
        conversionType,
        fromUnit,
        previousPeriodConsumption,
        toUnit
      ).Value;
      baselineConsumptionData.push(unitScaledPrevious ?? 0);
      baselineCostData.push(previousPeriodCost ?? 0);
      const unitScaledActual = getConvertedValue(
        conversionType,
        fromUnit,
        totalActualConsumption,
        toUnit
      ).Value;
      actualConsumptionData.push(unitScaledActual ?? 0);
      actualCostData.push(totalActualCost ?? 0);
    }
  );
  return {
    baselineConsumptionData,
    baselineCostData,
    actualConsumptionData,
    actualCostData,
    MonthIndex,
    YearIndex,
    IsCompleted,
    PeriodUpto
  };
};

export const convertDataForUtilitycost = (
  breakdownByUtility: Array<IUtilityProps>,
  showChangeWhenData: boolean = false
) => {
  const UtilitySavingsData: Array<breakdownUtilityProps> = [];

  breakdownByUtility.forEach((data) => {
    const {
      type,
      totalActualCost,
      totalActualCostPercentage,
      previousPeriodCost
    } = data;
    const savingPercent =
      showChangeWhenData &&
      (isNaN(Number(totalActualCost)) ||
        isNaN(Number(previousPeriodCost)) ||
        Number(totalActualCost) === 0 ||
        Number(previousPeriodCost) === 0 ||
        Math.abs(Number(totalActualCostPercentage)) > PERCENT_CHANGE_THRESHOLD);
    let meterConsumptions: breakdownUtilityProps = {
      Type: type,
      TotalActualCost: totalActualCost,
      // TotalActualCostPercentage: savingPercent
      //   ? null
      //   : totalActualCostPercentage,
      TotalActualCostPercentage: totalActualCostPercentage,
      previousPeriodCost: previousPeriodCost
    };
    UtilitySavingsData.push(meterConsumptions);
  });
  return UtilitySavingsData;
};

export const convertDataForCarbonSavingsMeter = (
  emissionsByUtility: Array<IEmissionsByUtility | IFuelType>,
  unit: string,
  toggleFeatureScopeOne: boolean = false,
  showChangeWhenData: boolean = false
) => {
  const valuesArray = getCarbonTotalEmissionValues(emissionsByUtility);
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.CARBON,
    unit,
    valuesArray
  );
  const carbonsavingsData: Array<ISavingsMeterProps> = [];
  emissionsByUtility?.slice(0, MAGIC_VALUE.THREE).forEach((data: any) => {
    const {
      utilityType,
      totalActualEmission,
      totalDeviation,
      totalDeviationPercentage,
      totalPreviousPeriodEmission
    } = data;
    const convertedHigh = totalActualEmission;
    const perChange = totalDeviationPercentage?.toFixed(
      KPI_PERCENTAGE_DECIMAL_PRECISION
    );
    const deviationUnit = getMaxConvertedValueFromArray(
      ConversionType.CARBON,
      unit,
      [totalDeviation]
    ).Unit;
    let meterSavings: ISavingsMeterProps = {
      ConsumptionTypeName: toggleFeatureScopeOne ? data.scope : utilityType!,
      ConsumptionHigh:
        totalActualEmission !== null ? convertedHigh.toString() : null,
      deviationPercentage: totalDeviationPercentage?.toFixed(
        KPI_PERCENTAGE_DECIMAL_PRECISION
      ),
      ConsumptionDeviation:
        showChangeWhenData &&
        (isNaN(Number(totalPreviousPeriodEmission)) ||
          isNaN(Number(totalActualEmission)) ||
          Number(totalActualEmission) === 0 ||
          Number(totalPreviousPeriodEmission) === 0 ||
          Math.abs(Number(perChange)) > PERCENT_CHANGE_THRESHOLD)
          ? null
          : totalDeviation,
      TotalDeviation: getConvertedValue(
        ConversionType.CARBON,
        unit,
        totalDeviation,
        maxValueUnitConversion.Unit
      )?.Value,
      unit: getMaxConvertedValueFromArray(ConversionType.CARBON, unit, [
        totalActualEmission
      ]).Unit,
      totalDeviationUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        unit,
        [totalDeviation]
      ).Unit,
      deviationUnit: deviationUnit
    };

    carbonsavingsData.push(meterSavings);
  });
  return {
    values: carbonsavingsData,
    displayUnit: maxValueUnitConversion.Unit
  };
};

export const convertUtilityServiceToOption = (
  services: Array<IServiceProvider> | undefined
): Array<IOption> => {
  const serviceOptions: Array<IOption> = [];
  if (services) {
    services.forEach((service: IServiceProvider) => {
      let serviceOption: IOption = {
        text: '',
        value: ''
      };
      serviceOption.text = service.utilityProviderName;
      serviceOption.value = service.utilityProviderId;
      serviceOptions.push(serviceOption);
    });
  }
  return serviceOptions;
};
export const capitalize = (str: string) => {
  return str.replace(/^\w/, (c) => c.toUpperCase());
};

export const truncate = (str: string, position: number = MAGIC_VALUE.TEN) => {
  return str.length > position + MAGIC_VALUE.THREE
    ? str.substring(0, position) + '...'
    : str;
};
export const formatBytes = (bytes: number, decimals: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const validateWholeNumber = (text: string) => {
  const [curr, ...prev] = text.split('').reverse();
  const parseResult = parseInt(curr, 10);
  if (isNaN(parseResult)) {
    const reversed = [...prev].reverse();
    return reversed.join('');
  } else {
    return text;
  }
};

export const debounce = (fn: Function, ms: number) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function(this: IAny, ...args: IAny[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};

export const convertUtilityProvidersToOptions = (
  UtilityProviderList: IUtilityProviders[]
) => {
  if (!UtilityProviderList) {
    return [];
  }
  return UtilityProviderList.map((provider: IUtilityProviders) => {
    return {
      text: provider.utilityProviderName,
      value: JSON.stringify(provider)
    };
  });
};

export const dataAvailableCheck = (isDataAvailable: IEuiResponse) =>
  isDataAvailable ? true : false;

export const utilityCostCkeck = (utilityCostCurrency: string, unit: string) =>
  (!utilityCostCurrency && unit && unit.length) || 0;
export const progressBarCheck = (progressBarActual: string) =>
  (progressBarActual && progressBarActual.length) || 0;

export const convertEuiContentCheck = (
  message: string,
  _convertedEuiContent: string
) => (message ? '--' : _convertedEuiContent);
export const convertEuiUnitCheck = (
  message: string,
  _convertedEuiUnit: string
) => (message ? '' : _convertedEuiUnit);
export const progressSavingCheck = (
  savings: number | null,
  progressBarSavingsPercentage: string
) =>
  (savings &&
    progressBarSavingsPercentage &&
    progressBarSavingsPercentage.length) ||
  0;

const scaleFactor: IAny = {
  therm: 0.1,
  'kilowatt hour': 0.003412,
  'kilowatt hours': 0.003412,
  kilowatthour: 0.003412,
  kilowatt_hour: 0.003412,
  kilowatt_hours: 0.003412,
  scf: 0.001028,
  'standard cubic feet': 0.001028,
  standardcubicfeet: 0.001028,
  'standard cubic foot': 0.001028,
  standardcubicfoot: 0.001028,
  standard_cubic_feet: 0.001028,
  standard_cubic_foot: 0.001028,
  'million btu': 1,
  millionbtu: 1,
  mmbtu: 1,
  'cubic meter': 0.03661,
  cubic_meter: 0.03661,
  'cubic meters': 0.03661,
  cubic_meters: 0.03661,
  'standard cubic meter': 0.0332005312,
  mega_joules: 0.000947817,
  'thousand cubic feet': 1.038,
  'centum cubic feet': 0.1037
};

export const convertDataForSavingsMeter = (
  utilityMeters: Array<IMeterSavings>,
  fromUnit: string
) => {
  const meterSavingsData: Array<IMeterSavings> = [];
  let utilityToUnit: IObject = {};
  let utilityFromUnit: IObject = {};

  utilityMeters.forEach((data) => {
    const {
      totalActualConsumption,
      totalActualCost,
      previousPeriodConsumption,
      previousPeriodCost,
      costSavings,
      costSavingsPercentage,
      consumptionSavings,
      consumptionSavingsPercentage,
      type,
      measurementUnit
    } = data;

    const valuesArray = [totalActualConsumption, previousPeriodConsumption];

    let isElectricOrGasUtility =
      type === UtilityType.Electricity ||
      type.toLowerCase() === UtilityType.Electric ||
      type === UtilityType.Gas ||
      type.toLowerCase() === UtilityType.Gas.toLowerCase();

    // let isElectricUtility =
    //   type === UtilityType.Electricity ||
    //   type.toLowerCase() === UtilityType.Electric;
    const maxValueUnitConversion = getMaxConvertedValueFromArray(
      type,
      measurementUnit, //measurementUnit,
      valuesArray,
      measurementUnit
    );

    let meterSavings = {
      // meter: meterNumber ? meterNumber : meterId,
      totalActualConsumption: totalActualConsumption,
      meterType: type,
      actualUnit: getMaxConvertedValueFromArray(
        type,
        measurementUnit, //measurementUnit,
        [totalActualConsumption],
        measurementUnit
      ).Unit,
      totalActualCost,
      previousPeriodConsumption: previousPeriodConsumption,
      baselineUnit: getMaxConvertedValueFromArray(
        type,
        measurementUnit, //measurementUnit,
        [previousPeriodConsumption],
        measurementUnit
      ).Unit,
      previousPeriodCost,
      costSavings,
      consumptionSavings: consumptionSavings,
      changeUnit: getMaxConvertedValueFromArray(
        type,
        measurementUnit, //measurementUnit,
        [consumptionSavings],
        measurementUnit
      ).Unit,
      percentageCostSavings: costSavingsPercentage!,
      percentageConsumptionSaving: consumptionSavingsPercentage,
      type,
      unit: maxValueUnitConversion.Unit,
      fromUnit: measurementUnit
    };
    meterSavingsData.push(meterSavings as IMeterSavings);
    const key = type;
    utilityFromUnit[key] = measurementUnit;
    utilityToUnit[key] = maxValueUnitConversion.Unit;
  });
  return {
    meterSavingdata: meterSavingsData,
    utilityFromUnit: utilityFromUnit,
    utilityToUnit: utilityToUnit
  };
};
export const convertDataForUtilitySavingsMeter = (
  utilityMeters: Array<IUtilityCost>,
  fromUnit: string,
  toUnit: string,
  conversionType: string,
  showChangeWhenData: boolean = false
) => {
  let meterSavingsData: Array<IUtilityMeterSavings> = [];
  utilityMeters.forEach((data) => {
    const { meterType, aggregations, meterName } = data;
    const {
      totalActualConsumption,
      totalActualCost,
      previousPeriodConsumption,
      previousPeriodCost,
      totalCostSavings,
      totalCostSavingsPercentage,
      totalConsumptionSavings,
      totalConsumptionSavingsPercentage
    } = aggregations;

    const hasBaseline =
      showChangeWhenData &&
      (isNaN(Number(totalActualConsumption)) ||
        isNaN(Number(previousPeriodConsumption)) ||
        Number(previousPeriodConsumption) === 0 ||
        Number(totalActualConsumption) === 0 ||
        Math.abs(Number(totalConsumptionSavingsPercentage)) >
          PERCENT_CHANGE_THRESHOLD);

    const savingPercent =
      showChangeWhenData &&
      (isNaN(Number(totalActualCost)) ||
        isNaN(Number(previousPeriodCost)) ||
        Number(totalActualCost) === 0 ||
        Number(previousPeriodCost) === 0 ||
        Math.abs(Number(totalCostSavingsPercentage)) >
          PERCENT_CHANGE_THRESHOLD);

    let meterSavings: IUtilityMeterSavings = {
      meter: meterName,
      totalActualConsumption: totalActualConsumption,
      actualUnit: getMaxConvertedValueFromArray(
        conversionType,
        fromUnit,
        [totalActualConsumption],
        fromUnit
      ).Unit,
      totalActualCost,
      previousPeriodConsumption: previousPeriodConsumption,
      baselineUnit: getMaxConvertedValueFromArray(
        conversionType,
        fromUnit,
        [previousPeriodConsumption],
        fromUnit
      ).Unit,
      previousPeriodCost,
      totalCostSavings: savingPercent ? null : totalCostSavings,
      totalConsumptionSavings: hasBaseline ? null : totalConsumptionSavings,
      changeUnit: getMaxConvertedValueFromArray(
        conversionType,
        fromUnit,
        [totalConsumptionSavings],
        fromUnit
      ).Unit,
      percentageCostSavings: totalCostSavingsPercentage,
      percentageConsumptionSaving: totalConsumptionSavingsPercentage,
      meterType: meterType
    };
    meterSavingsData.push(meterSavings);
  });
  meterSavingsData = addRelativeCostAndConsupValue(
    meterSavingsData
  ) as IUtilityMeterSavings[];
  return meterSavingsData;
};

const addRelativeCostAndConsupValue = (
  meterSavingsData: IUtilityMeterSavings[] | IUtilityCostSavings[]
) => {
  let maxRelativeValue: number | null = Number.MIN_VALUE;
  let maxRelativeCoseValue = Number.MIN_VALUE;
  meterSavingsData.forEach((meter) => {
    const tempMax =
      meter.totalActualConsumption! > meter.previousPeriodConsumption!
        ? meter.totalActualConsumption
        : meter.previousPeriodConsumption;
    maxRelativeValue =
      tempMax! > maxRelativeValue! ? tempMax : maxRelativeValue;
    const tempMaxCost =
      meter.totalActualCost > meter.previousPeriodCost
        ? meter.totalActualCost
        : meter.previousPeriodCost;
    maxRelativeCoseValue =
      tempMaxCost > maxRelativeCoseValue ? tempMaxCost : maxRelativeCoseValue;
  });
  return meterSavingsData.map((meterdata) => {
    return {
      ...meterdata,
      progessBarRelativeWidth: maxRelativeValue,
      progessBarRelativeWidthCost: maxRelativeCoseValue
    };
  });
};
export const getViewDetailsStartEndDate = () => {
  let selectedTimeSpan: Date = new Date();
  selectedTimeSpan.setDate(new Date().getDate() - MAGIC_VALUE.ONEHUNDREDEIGHTY);
  selectedTimeSpan = new Date(
    selectedTimeSpan.getFullYear(),
    selectedTimeSpan.getMonth(),
    1
  );

  const endTime = new Date().toISOString();
  return { startTime: selectedTimeSpan, endTime: endTime };
};

export const getDateFormat = () => {
  return 'MM/DD/YYYY';
};
export const getUtilityAccountMessageKey = (
  status: UtilityAccountStatus | IDSStatus
): IMessageDescriptor => {
  let messageKey: IMessageDescriptor;
  messageKey = UtilityStatusMessageKey[status];
  if (messageKey) return messageKey;
  else return descriptors.urjanetStatus.ERROR;
};

export const getUtilityAccountMessageDescription = (
  status: UtilityAccountStatus | IDSStatus
): IMessageDescriptor => {
  let messageKey: IMessageDescriptor;
  messageKey = UtilityMessageMapping[status];
  if (messageKey) return messageKey;
  else return descriptors.urjanetStatus.ERRORMESSAGE;
};

export const getPlugStatusMessageDescription = (
  status: string,
  statusDetail: string
) => {
  if (PlugMessageMappingWithDetail[status]) {
    if (
      statusDetail &&
      statusDetail !== '' &&
      PlugMessageMappingWithDetail[status][statusDetail]
    )
      return PlugMessageMappingWithDetail[status][statusDetail];
    return PlugMessageMappingWithDetail[status].default;
  }
  return descriptors.urjanetStatus.ERRORMESSAGE;
};

export const isUtilityProviderEnrolled = (
  status: UtilityAccountStatus
): boolean => {
  return [
    'SUCCESSFUL_ENROLLED',
    'SUCCESSFUL_DELIVERED',
    'PRODUCTION',
    'DAQ_PRODUCTION',
    'Active'
  ].includes(status);
};

export const getUtilityAccountStatusImage = (
  status: UtilityAccountStatus | IDSStatus
): JSX.Element => {
  const successImageStatuses: string[] = [
    'SUCCESSFUL_ENROLLED',
    'SUCCESSFUL_DELIVERED',
    'PRODUCTION',
    'DAQ_PRODUCTION',
    'Active',
    'RE_ENROLLED',
    //'Enroll_Successful'
    // IDS Statuses
    'CONNECTION_SUCCESS',
    'ACTIVE',
    'NEW_ACCOUNT'
  ];
  const ErrorImageStatuses: string[] = [
    'BLOCKED',
    'SUCCESSFUL_NOT_ENROLLED',
    'SOURCE_FORMAT_CHANGE',
    'ACCOUNT_NUMBER_CHANGE',
    'USER_ACTION_REQUIRED',
    'STATEMENT_FAILED',
    'CAPTCHA_LOGIN',
    'STATEMENT_MISSING',
    'TEMPLATE_FAILURE',
    'FINALLED',
    'COMMON_ERROR',
    'FAILED_TO_ACCESS',
    'Connectivity_Err',
    'WEBSITE_ISSUE',
    'NAVIGATION_FAILURE',
    'REJECTED',
    'ENROLLMENT_FAILURE',
    'DOES_NOT_EXIST',
    'ACCOUNT_MISSING',
    'MetersMismatch',
    'INACTIVE',
    'RE_ENROLLED_REQUIRED',
    'FAILED_LOGIN',
    'NEEDS_CREDENTIAL',
    'ACCOUNT_MISSING',
    'USER_ACTION_REQUIRED',
    'ACCOUNT_MISSING',
    'LOGIN_FAILURE',
    'NEEDS_TEMPLATE',
    // IDS Statuses
    'DATA_ACCESS_FAILURE',
    'CONNECTION_FAILURE'
  ];
  const deactiveImgStatus: string[] = ['DEACTIVATED'];
  if (successImageStatuses.includes(status))
    return <img className="img-account-success" src={AccountSuccess} />;
  else if (ErrorImageStatuses.includes(status))
    return <img className="img-account-error" src={AccountError} />;
  else if (deactiveImgStatus.includes(status))
    return (
      <Icon
        className="utility-deactivated"
        root="common"
        name="badge-stop"
        size="small"
      />
    );
  else return <img className="img-account-warning" src={AccountWarning} />;
};
export const getFirstDayofSelectedMonth = (date: string) => {
  return moment(date)
    .startOf('month')
    .format('YYYY-MM-DD');
};
export const getLastDayofSelectedMonth = (date: string) => {
  return moment(date)
    .endOf('month')
    .format('YYYY-MM-DD HH:mm');
};
export const getDateFromDatetime = (date: string) => {
  return moment(date).format('YYYY-MM-DD');
};
export const getEuiCalculationDate = () => {
  return moment()
    .add(-1, 'month')
    .startOf('month')
    .format('YYYY-MM-DD');
};
export const convertDataForBreakdownByMeter = (
  breakdownByMeter: Array<IBreakdownByMeterProps> | Array<AreaWiseAggregations>,
  unit: string,
  summaryValues: (number | null)[] = [],
  originalUnit: string
) => {
  const valuesArray = getEnergyTotalUsagesValues(breakdownByMeter);
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.ELECTRICITY,
    unit,
    [...valuesArray, ...summaryValues]
  );
  const EnergyBreakdownData: Array<IMeterDataProps> = [];

  breakdownByMeter.forEach((data) => {
    const {
      name,
      totalConsumption,
      totalEnergyDeviation,
      totalEnergyDeviationPercentage
    } = data;
    const high = getConvertedValue(
      ConversionType.ELECTRICITY,
      originalUnit, //unit,
      totalConsumption,
      maxValueUnitConversion.Unit
    );

    let meterConsumptions: IMeterDataProps = {
      type: name,
      high: totalConsumption !== null && high ? high?.Value?.toString() : null,
      change:
        totalEnergyDeviation === null
          ? null
          : getConvertedValue(
              ConversionType.ELECTRICITY,
              originalUnit, //unit,
              totalEnergyDeviation,
              maxValueUnitConversion.Unit
            ).Value,
      totalDeviation:
        totalEnergyDeviation === null
          ? null
          : getConvertedValue(
              ConversionType.ELECTRICITY,
              originalUnit, //unit,
              totalEnergyDeviation,
              maxValueUnitConversion.Unit
            ).Value,
      totalDeviationPercentage:
        totalEnergyDeviationPercentage === null
          ? null
          : parseFloat(
              totalEnergyDeviationPercentage?.toFixed(
                KPI_PERCENTAGE_DECIMAL_PRECISION
              )
            )
    };
    EnergyBreakdownData.push(meterConsumptions);
  });
  return {
    values: EnergyBreakdownData,
    displayUnit: maxValueUnitConversion.Unit
  };
};

export const convertDataForBreakdownByMeterWithIndividualScaling = (
  breakdownByMeter: Array<IBreakdownByMeterProps> | Array<AreaWiseAggregations>,
  unit: string,
  summaryValues: (number | null)[] = [],
  originalUnit: string,
  showChangeWhenData: boolean = false,
  conversionType: string = ConversionType.ELECTRICITY
) => {
  const valuesArray = getEnergyTotalUsagesValues(breakdownByMeter);
  // const maxValueUnitConversion = getMaxConvertedValueFromArray(
  //   ConversionType.ELECTRICITY,
  //   unit,
  //   [...valuesArray, ...summaryValues]
  // );
  const EnergyBreakdownData: Array<IMeterDataProps> = [];

  breakdownByMeter &&
    breakdownByMeter.forEach((data) => {
      const {
        name,
        totalConsumption,
        totalBaselineConsumption,
        totalEnergyDeviation,
        totalEnergyDeviationPercentage
      } = data;

      const high = totalConsumption;

      const hasBaselineOrConsumptionData =
        showChangeWhenData &&
        (isNaN(Number(totalConsumption)) ||
          isNaN(Number(totalBaselineConsumption)) ||
          Number(totalConsumption) === 0 ||
          Number(totalBaselineConsumption) === 0 ||
          Math.abs(Number(totalEnergyDeviationPercentage)) >
            PERCENT_CHANGE_THRESHOLD);

      let meterConsumptions: IMeterDataProps = {
        type: name,
        conversionType,
        high: totalConsumption !== null && high ? high.toString() : null,
        unit: getMaxConvertedValueFromArray(
          conversionType,
          unit,
          [totalConsumption],
          originalUnit
        ).Unit,
        change:
          hasBaselineOrConsumptionData || totalEnergyDeviation === null
            ? null
            : totalEnergyDeviation,
        deviationUnit: getMaxConvertedValueFromArray(
          conversionType,
          unit,
          [totalEnergyDeviation],
          originalUnit
        ).Unit,
        totalDeviation:
          hasBaselineOrConsumptionData || totalEnergyDeviation === null
            ? null
            : totalEnergyDeviation,
        totalDeviationPercentage:
          totalEnergyDeviationPercentage === null
            ? null
            : parseFloat(
                totalEnergyDeviationPercentage?.toFixed(
                  KPI_PERCENTAGE_DECIMAL_PRECISION
                )
              )
      };
      EnergyBreakdownData.push(meterConsumptions);
    });
  return {
    values: EnergyBreakdownData
    // displayUnit: maxValueUnitConversion.Unit
  };
};

export const getPerformanceAggregationValues = (
  performanceAggregation: Array<IPerformanceAggregationProps>
) => {
  return performanceAggregation.map((value) => {
    const convertedActual = !value.consumption ? 0 : value.consumption;
    const convertedBaseline = !value.baselineConsumption
      ? 0
      : value.baselineConsumption;
    if (value && (value.consumption || value.baselineConsumption)) {
      return Math.max(convertedActual, convertedBaseline);
    } else {
      return 0;
    }
  });
};

export const getPerformanceAggregationValuesV2 = (
  performanceAggregation: Array<EnergyUsageAggregation>
) => {
  return performanceAggregation?.map((value) => {
    const convertedActual = !value?.totalConsumption
      ? 0
      : value?.totalConsumption;
    const convertedBaseline = !value.totalBaselineConsumption
      ? 0
      : value.totalBaselineConsumption;
    if (value && (value.totalConsumption || value.totalBaselineConsumption)) {
      return Math.max(convertedActual, convertedBaseline);
    } else {
      return 0;
    }
  });
};

export const convertUnitForPerformanceGraph = (
  performanceAggregation: Array<IPerformanceAggregationProps>,
  unit: string,
  originalUnit?: string,
  conversionType: string = ConversionType.ELECTRICITY
) => {
  const valuesArray = getPerformanceAggregationValues(performanceAggregation);
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    conversionType,
    unit,
    [...valuesArray],
    originalUnit
  );

  return {
    displayUnit: maxValueUnitConversion.Unit
  };
};

export const convertUnitForPerformanceGraphV2 = (
  performanceAggregation: Array<EnergyUsageAggregation>,
  unit: string
) => {
  const valuesArray = getPerformanceAggregationValuesV2(performanceAggregation);
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.ELECTRICITY,
    unit,
    [...valuesArray]
  );

  return {
    displayUnit: maxValueUnitConversion.Unit
  };
};

export const getSpanDetails = (
  aggregationsStartDate: string,
  aggregationsEndDate: string
) => {
  const startMonth = moment(aggregationsStartDate).format('MMM');
  const startYear = moment(aggregationsStartDate).format('YYYY');
  const endMonth = moment(aggregationsEndDate).format('MMM');
  const endYear = moment(aggregationsEndDate).format('YYYY');
  const startDate = moment(aggregationsStartDate).format('DD');
  const endDate = moment(aggregationsEndDate).format('DD');
  return { startMonth, startYear, endMonth, endYear, startDate, endDate };
};

export const getPerformanceGraphData = (
  performanceAggregation: Array<IPerformanceAggregationProps>,
  fromUnit: string,
  toUnit: string,
  aggreagationType: string,
  conversionType: string
) => {
  let actualConsumptionData: IArraySeries = [];
  let baselineConsumptionData: IArraySeries = [];
  let consumptionUnitDisplay: string;
  performanceAggregation.forEach((x: IPerformanceAggregationProps) => {
    const dateValue = getValueFromDate(x.consumptionDate, aggreagationType);
    actualConsumptionData.push([
      dateValue,
      getConvertedValue(conversionType, fromUnit, x.consumption, toUnit).Value
    ]);
    baselineConsumptionData.push([
      dateValue,
      getConvertedValue(conversionType, fromUnit, x.baselineConsumption, toUnit)
        .Value
    ]);
  });
  consumptionUnitDisplay = toUnit;
  return {
    actualConsumptionData,
    baselineConsumptionData,
    consumptionUnitDisplay
  };
};

export const getPerformanceGraphDataNew = (
  performanceAggregation: Array<IPerformanceAggregationProps>,
  fromUnit: string,
  toUnit: string,
  aggreagationType: string,
  conversionType: string,
  aggregationsStartDate: string,
  aggregationsEndDate: string
) => {
  const aggregatedDataMap: { [key: string]: IPerformanceAggregationProps } = {};
  performanceAggregation.forEach((x: IPerformanceAggregationProps) => {
    aggregatedDataMap[x.consumptionDate] = x;
  });
  const currentDate = moment(aggregationsStartDate);
  const endDate = moment(aggregationsEndDate);
  const processedAggregation: IPerformanceAggregationProps[] = [];
  while (currentDate.isSameOrBefore(endDate)) {
    const date = currentDate.format('YYYY-MM-DDTHH:mm:ss');
    if (aggregatedDataMap[date]) {
      processedAggregation.push(aggregatedDataMap[date]);
    } else {
      processedAggregation.push({
        consumption: 0,
        baselineConsumption: 0,
        consumptionDate: date
      });
    }
    currentDate.add(1, 'day');
  }
  const actualConsData: IArraySeries = [];
  const baselineConsDate: IArraySeries = [];
  const consumptionUnitDisplay: string = toUnit;
  processedAggregation.forEach((x: IPerformanceAggregationProps) => {
    const dateValue = getValueFromDate(x.consumptionDate, aggreagationType);
    actualConsData.push([
      dateValue,
      getConvertedValue(conversionType, fromUnit, x.consumption, toUnit).Value
    ]);
    baselineConsDate.push([
      dateValue,
      getConvertedValue(conversionType, fromUnit, x.baselineConsumption, toUnit)
        .Value
    ]);
  });
  return {
    actualConsumptionData: actualConsData,
    baselineConsumptionData: baselineConsDate,
    consumptionUnitDisplay
  };
};

export const getPerformanceGraphDataV2 = (
  performanceAggregation: Array<EnergyUsageAggregation>,
  fromUnit: string,
  toUnit: string,
  aggreagationType: string,
  conversionType: string
) => {
  let actualConsumptionData: IArraySeries = [];
  let baselineConsumptionData: IArraySeries = [];
  let consumptionUnitDisplay: string;
  performanceAggregation &&
    performanceAggregation.length &&
    performanceAggregation.forEach((x: EnergyUsageAggregation) => {
      const dateValue = getValueFromDate(x.usageDateTime, aggreagationType);
      actualConsumptionData.push([
        dateValue,
        getConvertedValue(conversionType, fromUnit, x.totalConsumption, toUnit)
          .Value
      ]);
      baselineConsumptionData.push([
        dateValue,
        getConvertedValue(
          conversionType,
          fromUnit,
          x.totalBaselineConsumption,
          toUnit
        ).Value
      ]);
    });
  consumptionUnitDisplay = toUnit;
  return {
    actualConsumptionData,
    baselineConsumptionData,
    consumptionUnitDisplay
  };
};

export const getValueFromDate = (date: string, aggregationType: string) => {
  if (aggregationType && aggregationType.toLowerCase() === 'hourly') {
    return moment(date).valueOf();
  } else {
    return moment(date)
      .startOf('day')
      .valueOf();
  }
};

export const getUtilityTypeMeterDetails = (
  utilityMeters: Array<UtilityMeterData>,
  utilityToUnit: IObject,
  utilityFromUnit: IObject,
  fromUnit: string
) => {
  const electricityMeters: Array<UtilityMeterData> = [];
  const gasMeters: Array<UtilityMeterData> = [];
  const waterMeters: Array<UtilityMeterData> = [];
  const severageMeters: Array<UtilityMeterData> = [];

  utilityMeters.forEach((data) => {
    const { meterId, meterNumber, meterType, aggregations, meterName } = data;

    if (
      meterType.includes(UtilityType.Electricity) ||
      meterType.toLowerCase().includes(UtilityType.Electric)
    ) {
      let meterSavings = {
        meterId: meterId,
        meterNumber: meterNumber,
        meterName: meterName,
        meterType: meterType,
        fromUnit: utilityFromUnit[meterType],
        totalActualConsumption: aggregations.totalActualConsumption,
        actualUnit: getMaxConvertedValueFromArray(
          ConversionType.ELECTRICITY,
          utilityFromUnit[meterType], //measurementUnit,
          [aggregations.totalActualConsumption],
          utilityFromUnit[meterType]
        ).Unit,
        previousPeriodConsumption: aggregations.previousPeriodConsumption,
        baselineUnit: getMaxConvertedValueFromArray(
          ConversionType.ELECTRICITY,
          utilityFromUnit[meterType], //measurementUnit,
          [aggregations.previousPeriodConsumption],
          utilityFromUnit[meterType]
        ).Unit,
        percentageConsumptionSaving:
          aggregations.totalConsumptionSavingsPercentage,
        totalActualCost: aggregations.totalActualCost,
        previousPeriodCost: aggregations.previousPeriodCost,
        percentageCostSavings: aggregations.totalCostSavingsPercentage,
        consumptionSavings: aggregations.totalConsumptionSavings,
        changeUnit: getMaxConvertedValueFromArray(
          ConversionType.ELECTRICITY,
          utilityFromUnit[meterType], //measurementUnit,
          [aggregations.totalConsumptionSavings],
          utilityFromUnit[meterType]
        ).Unit,
        costSavings: aggregations.totalCostSavings,
        unit: utilityToUnit[meterType]
      };
      electricityMeters.push(meterSavings as IAny);
    }

    if (meterType.includes(UtilityType.Gas)) {
      let meterSavings = {
        meterId: meterId,
        meterNumber: meterNumber,
        meterType: meterType,
        meterName: meterName,
        fromUnit: utilityFromUnit[meterType],
        totalActualConsumption: aggregations.totalActualConsumption,
        actualUnit: getMaxConvertedValueFromArray(
          ConversionType.GAS,
          utilityFromUnit[meterType], //measurementUnit,
          [aggregations.totalActualConsumption],
          utilityFromUnit[meterType]
        ).Unit,
        previousPeriodConsumption: aggregations.previousPeriodConsumption,
        baselineUnit: getMaxConvertedValueFromArray(
          ConversionType.GAS,
          utilityFromUnit[meterType], //measurementUnit,
          [aggregations.previousPeriodConsumption],
          utilityFromUnit[meterType]
        ).Unit,
        percentageConsumptionSaving:
          aggregations.totalConsumptionSavingsPercentage,
        totalActualCost: aggregations.totalActualCost,
        previousPeriodCost: aggregations.previousPeriodCost,
        percentageCostSavings: aggregations.totalCostSavingsPercentage,
        consumptionSavings: aggregations.totalConsumptionSavings,
        changeUnit: getMaxConvertedValueFromArray(
          ConversionType.GAS,
          utilityFromUnit[meterType], //measurementUnit,
          [aggregations.totalConsumptionSavings],
          utilityFromUnit[meterType]
        ).Unit,
        costSavings: aggregations.totalCostSavings,
        unit: utilityToUnit[meterType]
      };
      gasMeters.push(meterSavings as IAny);
    }
    if (meterType.includes(UtilityType.Water)) {
      let meterSavings = {
        meterId: meterId,
        meterNumber: meterNumber,
        meterName: meterName,
        meterType: meterType,
        fromUnit: utilityFromUnit[meterType],
        totalActualConsumption: aggregations.totalActualConsumption,
        actualUnit: getMaxConvertedValueFromArray(
          ConversionType.WATER,
          utilityFromUnit[meterType], //measurementUnit,
          [aggregations.totalActualConsumption],
          utilityFromUnit[meterType]
        ).Unit,
        previousPeriodConsumption: aggregations.previousPeriodConsumption,
        baselineUnit: getMaxConvertedValueFromArray(
          ConversionType.WATER,
          utilityFromUnit[meterType], //measurementUnit,
          [aggregations.previousPeriodConsumption],
          utilityFromUnit[meterType]
        ).Unit,
        percentageConsumptionSaving:
          aggregations.totalConsumptionSavingsPercentage,
        totalActualCost: aggregations.totalActualCost,
        previousPeriodCost: aggregations.previousPeriodCost,
        percentageCostSavings: aggregations.totalCostSavingsPercentage,
        consumptionSavings: aggregations.totalConsumptionSavings,
        changeUnit: getMaxConvertedValueFromArray(
          ConversionType.WATER,
          utilityFromUnit[meterType], //measurementUnit,
          [aggregations.totalConsumptionSavings],
          utilityFromUnit[meterType]
        ).Unit,
        costSavings: aggregations.totalCostSavings,
        unit: utilityToUnit[meterType]
      };
      waterMeters.push(meterSavings as IAny);
    }
    if (
      meterType.includes(UtilityType.Sewerage) ||
      meterType.includes(UtilityType.Sewer)
    ) {
      let meterSavings = {
        meterId: meterId,
        meterNumber: meterNumber,
        meterName: meterName,
        meterType: meterType,
        fromUnit: utilityFromUnit[meterType],
        totalActualConsumption: aggregations.totalActualConsumption,

        actualUnit: getMaxConvertedValueFromArray(
          ConversionType.SEWERAGE,
          utilityFromUnit[meterType], //measurementUnit,
          [aggregations.totalActualConsumption],
          utilityFromUnit[meterType]
        ).Unit,
        previousPeriodConsumption: aggregations.previousPeriodConsumption,
        baselineUnit: getMaxConvertedValueFromArray(
          ConversionType.SEWERAGE,
          utilityFromUnit[meterType], //measurementUnit,
          [aggregations.previousPeriodConsumption],
          utilityFromUnit[meterType]
        ).Unit,
        percentageConsumptionSaving:
          aggregations.totalConsumptionSavingsPercentage,
        totalActualCost: aggregations.totalActualCost,
        previousPeriodCost: aggregations.previousPeriodCost,
        percentageCostSavings: aggregations.totalCostSavingsPercentage,
        consumptionSavings: aggregations.totalConsumptionSavings,
        changeUnit: getMaxConvertedValueFromArray(
          ConversionType.SEWERAGE,
          utilityFromUnit[meterType], //measurementUnit,
          [aggregations.totalConsumptionSavings],
          utilityFromUnit[meterType]
        ).Unit,
        costSavings: aggregations.totalCostSavings,
        unit: utilityToUnit[meterType]
      };
      severageMeters.push(meterSavings as IAny);
    }
  });
  return { electricityMeters, gasMeters, waterMeters, severageMeters };
};

export const getAllUtilityMeterDetails = (
  utilityMeterDetails: Array<AllUtilityDetails>,
  showChangeWhenData: boolean = false
) => {
  let utilityMeterDetail: Array<IUtilityAllMeterDetailData> = [];
  utilityMeterDetails.forEach((data) => {
    const { meterSavingdata, utilityTypeMeterDetails } = data;
    for (const meterSaving of meterSavingdata) {
      const hasBaseline =
        showChangeWhenData &&
        (isNaN(Number(meterSaving.totalActualConsumption)) ||
          isNaN(Number(meterSaving.previousPeriodConsumption)) ||
          Number(meterSaving.totalActualConsumption) === 0 ||
          Number(meterSaving.previousPeriodConsumption) === 0 ||
          Math.abs(Number(meterSaving.percentageConsumptionSaving)) >
            PERCENT_CHANGE_THRESHOLD);

      const savingPercent =
        showChangeWhenData &&
        (isNaN(Number(meterSaving.totalActualCost)) ||
          isNaN(Number(meterSaving.previousPeriodCost)) ||
          Number(meterSaving.totalActualCost) === 0 ||
          Number(meterSaving.previousPeriodCost) === 0 ||
          Math.abs(Number(meterSaving.percentageCostSavings)) >
            PERCENT_CHANGE_THRESHOLD);

      let commonUtilityData: IUtilityAllMeterDetailData = {
        ...meterSaving,
        totalActualConsumption: meterSaving.totalActualConsumption!,
        previousPeriodConsumption: meterSaving.previousPeriodConsumption!,
        percentageConsumptionSaving: meterSaving.percentageConsumptionSaving!,
        totalActualCost: meterSaving.totalActualCost,
        previousPeriodCost: meterSaving.previousPeriodCost,
        percentageCostSavings: meterSaving.percentageCostSavings,
        type: meterSaving.type,
        consumptionSavings: hasBaseline
          ? null
          : meterSaving.consumptionSavings!,
        costSavings: savingPercent ? null : meterSaving.costSavings!,
        utilityMeters: utilityTypeMeterDetails.electricityMeters,
        unit: meterSaving.unit
      };
      if (
        meterSaving.type === UtilityType.Electricity ||
        meterSaving.type.toLowerCase() === UtilityType.Electric
      ) {
        utilityMeterDetail.push({
          ...commonUtilityData,
          utilityMeters: utilityTypeMeterDetails.electricityMeters
        });
      }
      if (meterSaving.type === UtilityType.Gas) {
        utilityMeterDetail.push({
          ...commonUtilityData,
          utilityMeters: utilityTypeMeterDetails.gasMeters
        });
      }
      if (meterSaving.type === UtilityType.Water) {
        utilityMeterDetail.push({
          ...commonUtilityData,
          utilityMeters: utilityTypeMeterDetails.waterMeters
        });
      }
      if (
        meterSaving.type === UtilityType.Sewerage ||
        meterSaving.type === UtilityType.Sewer
      ) {
        utilityMeterDetail.push({
          ...commonUtilityData,
          utilityMeters: utilityTypeMeterDetails.severageMeters
        });
      }
    }
  });
  let maxCost = Number.MIN_VALUE;
  utilityMeterDetail.forEach((meter: IUtilityAllMeterDetailData) => {
    let tempMax =
      meter.totalActualCost > meter.previousPeriodCost
        ? meter.totalActualCost
        : meter.previousPeriodCost;
    maxCost = tempMax > maxCost ? tempMax : maxCost;
  });
  utilityMeterDetail = utilityMeterDetail.map((meterDetails) => {
    return {
      ...meterDetails,
      progessBarRelativeWidthCost: maxCost,
      utilityMeters: meterDetails.utilityMeters.map(
        (meter: UtilityMeterData) => {
          const savingPercent =
            showChangeWhenData &&
            (isNaN(Number(meter.totalActualCost)) ||
              isNaN(Number(meter.previousPeriodCost)) ||
              Number(meter.totalActualCost) === 0 ||
              Number(meter.previousPeriodCost) === 0 ||
              Math.abs(Number(meter.percentageCostSavings)) >
                PERCENT_CHANGE_THRESHOLD);
          const hasBaseline =
            showChangeWhenData &&
            (isNaN(Number(meter.totalActualConsumption)) ||
              isNaN(Number(meter.previousPeriodConsumption)) ||
              Number(meter.totalActualConsumption) === 0 ||
              Number(meter.previousPeriodConsumption) === 0 ||
              Math.abs(Number(meter.percentageConsumptionSaving)) >
                PERCENT_CHANGE_THRESHOLD);
          return {
            ...meter,
            progessBarRelativeWidth:
              meterDetails.totalActualConsumption >
              meterDetails.previousPeriodConsumption
                ? meterDetails.totalActualConsumption
                : meterDetails.previousPeriodConsumption,
            progessBarRelativeWidthCost: maxCost,
            totalActualConsumption: meter.totalActualConsumption!,
            previousPeriodConsumption: meter.previousPeriodConsumption!,
            percentageConsumptionSaving: meter.percentageConsumptionSaving!,
            totalActualCost: meter.totalActualCost,
            previousPeriodCost: meter.previousPeriodCost,
            percentageCostSavings: meter.percentageCostSavings,
            consumptionSavings: hasBaseline ? null : meter.consumptionSavings!,
            costSavings: savingPercent ? null : meter.costSavings!,
            unit: meter.unit
          };
        }
      )
    };
  });
  return utilityMeterDetail;
};

export const isUpdateAction = (status: string) => {
  const updateActionStatuses = [
    'NEEDS_CREDENTIAL',
    'FAILED_LOGIN',
    'LOGIN_FAILURE',
    'ACCOUNT_MISSING',
    'CAPTCHA_LOGIN',
    'USER_ACTION_REQUIRED',
    'BLOCKED',
    'RE_ENROLLED_REQUIRED',
    'FAILED_TO_ACCESS'
  ];
  if (updateActionStatuses.includes(status)) return true;
  return false;
};

export const isDeactivedAction = (status: string) => {
  const deActivateActionStatuses = ['DEACTIVATED'];
  if (deActivateActionStatuses.includes(status)) return true;
  return false;
};
export const isFailedLogin = (status: string) => {
  const deActivateActionStatuses = [
    'FAILED_LOGIN',
    'NEEDS_CREDENTIAL',
    'ACCOUNT_MISSING',
    'USER_ACTION_REQUIRED',
    'ACCOUNT_MISSING',
    'LOGIN_FAILURE',
    'NEEDS_TEMPLATE',
    'BLOCKED',
    'RE_ENROLLED_REQUIRED'
  ];
  if (deActivateActionStatuses.includes(status)) return true;
  return false;
};

export const isDeactivedInProgressAction = (status: string) => {
  const deActivateActionStatuses = ['Deactivation_Initiated', 'CAPTCHA_LOGIN'];
  if (deActivateActionStatuses.includes(status)) return true;
  return false;
};

export const getActionModalInfo = (status: string, idsStatus?: string) => {
  const urjanetDescriptor = descriptors.energyManagement;
  if (
    status == 'NEEDS_CREDENTIAL' ||
    status == 'FAILED_LOGIN' ||
    status === 'RE_ENROLLED_REQUIRED'
  )
    return {
      header: urjanetDescriptor.ENTERCREDENTIALS,
      messageNote: urjanetDescriptor.ENTERCREDENTIALSNOTE,
      displayAccountInfo: false
    };
  if (status == 'LOGIN_FAILURE')
    return {
      header: urjanetDescriptor.UPDATECREDENTIALS,
      messageNote: urjanetDescriptor.UPDATECREDENTIALSNOTE,
      displayAccountInfo: false
    };
  if (status == 'Connectivity_Err' || isIDSConnectionError(idsStatus))
    return {
      header: urjanetDescriptor.REVERIFYHEADER,
      messageNote: urjanetDescriptor.REVERIFYNOTE,
      displayAccountInfo: true
    };
  if (status == 'ACCOUNT_MISSING')
    return {
      header: urjanetDescriptor.ACCOUNTMISSINGHEADER,
      messageNote: urjanetDescriptor.ACCOUNTMISSINGNOTE,
      displayAccountInfo: true
    };
  return { header: '', messageNote: '' };
};
export const isUploadDisabled = async (
  files: File[],
  minUploadFileSize: number,
  maxUploadFileSize: number,
  headers: string[]
) => {
  let isInvalid = false;
  if (files && files.length === 0) return true;
  for (let file of files) {
    if (!isInvalid) {
      if (file.size > maxUploadFileSize || file.size <= minUploadFileSize) {
        isInvalid = true;
      } else {
        isInvalid = await isCsvInvalid(file, headers);
      }
    }
  }
  return isInvalid;
};

export const isCsvInvalid = async (file: File, headers: string[]) => {
  try {
    const fileNameSplitArray = file && file.name && file.name.split('.');
    let checkName = /^[^*|\-":<>[\]{}`\\()';%!#@&$ ]+$/;
    let fileNameLength = fileNameSplitArray[0].length;
    if (fileNameSplitArray.length === 2) {
      let isValidName = checkName.test(file.name);
      return !isValidName;
    } else if (fileNameSplitArray.length > 2 || fileNameLength > 50) {
      return true;
    } else {
      console.log('');
    }
    if (fileNameSplitArray[fileNameSplitArray.length - 1] === 'csv') {
      const csvData = (await parseCsvData(file)) as string[][];
      let headersMatch =
        [...csvData[0]].sort().join(',') === [...headers].sort().join(',');
      return !headersMatch;
    } else {
      return false;
    }
  } catch (e) {
    return true;
  }
};

const parseCsvData = (file: File) =>
  new Promise((res, rej) => {
    // @ts-ignore
    PapaParse.parse(file, {
      complete: function(results) {
        if (results.errors && results.errors.length > 0) {
          rej(results.errors);
        } else {
          res(results.data);
        }
      },
      error: function(err) {
        rej(err);
      }
    });
  });

export const isDeactivateAvailabel = (status: string) => {
  const DeactivateOptionStatuses = [
    'SUCCESSFUL_ENROLLED',
    'SUCCESSFUL_DELIVERED',
    'PRODUCTION',
    'DAQ_PRODUCTION',
    'AGED_BILL',
    'Active',
    'Enroll_Successful',
    'NOMINATED',
    'MetersMismatch'
  ];
  if (DeactivateOptionStatuses.includes(status)) return true;
  else return false;
};
export const isIntegrationTypeAvailabel = (status: string) => {
  const DeactivateOptionStatuses = ['Automatic', 'Manual'];
  if (DeactivateOptionStatuses.includes(status)) return true;
  else return false;
};

export const getActionForUtilityStatus = (
  status: string,
  idsStatus?: string
) => {
  if (
    status == 'NEEDS_CREDENTIAL' ||
    status == 'FAILED_LOGIN' ||
    status == 'LOGIN_FAILURE' ||
    status == 'FAILED_TO_ACCESS' ||
    isIDSConnectionError(idsStatus)
  )
    return 'Credential';
  if (status == 'Connectivity_Err' || status === 'RE_ENROLLED_REQUIRED')
    return 'enroll';
  if (status == 'ACCOUNT_MISSING') return 'MissingAccount';
  return '';
};
export const getRequestBodyUtilityAction = (
  action: string,
  selectedUtility: IUtilityInfo,
  credentials: IUpdateAccount
) => {
  if (action == 'enroll' || action == 'Credential')
    return {
      UtilityAccountId: selectedUtility.id,
      Credential: btoa(`${credentials.username}:${credentials.password}`)
    };
  if (action == 'MissingAccount')
    return {
      UtilityAccountId: selectedUtility.id,
      Credential: btoa(`${credentials.username}:${credentials.password}`),
      NewAccountNumber: credentials.accountNumber
    };
};

export const getCategoryFromUtilityType = (utilityType: string) => {
  switch (utilityType) {
    case CarbonUtilityType.GAS:
      return CarbonUtilityTypeScope.GAS;
    case CarbonUtilityType.ELECTRICITY:
      return CarbonUtilityTypeScope.ELECTRICITY;
    case CarbonUtilityType.ELECTRIC:
      return CarbonUtilityTypeScope.ELECTRICITY;
    default:
      return CarbonUtilityTypeScope.INVALID;
  }
};

export const returnFormattedDateTime = (
  date: string,
  format?: string,
  time?: string
) => {
  if (format) {
    // return moment(date).format(format + ', hh:mm A');
    return moment(date).format(format + ' ' + time);
  }
  return moment(date).format('DD/MM/YYYY, hh:mm A');
};

export const returnCurrentTimeFormat = (date: string) => {
  return moment(date).format('YYYY-MM-DD HH:mm');
};

export const returnUTCTimeFormat = (date: string) => {
  return moment.utc(moment(date)).format('YYYY-MM-DD HH:mm');
};

export const isEnableIDSAvailable = (
  status: string,
  idsStatus?: IDSStatus | null
) => {
  const _idsAllowedUDSStatuses = [
    'SUCCESSFUL_ENROLLED',
    'SUCCESSFUL_DELIVERED',
    'PRODUCTION',
    'DAQ_PRODUCTION',
    'Active',
    'Enroll_Successful',
    'DEACTIVATED',
    'DEACTIVATION_INITIATED',
    'Deactivation_Initiated',
    'RE_ENROLL_INITIATED'
  ];
  const _idsStatusesForEnableOption: string[] = [
    // 'CONNECTION_FAILURE'
  ];
  return (
    (!idsStatus ||
      (idsStatus && _idsStatusesForEnableOption.includes(idsStatus))) &&
    _idsAllowedUDSStatuses.includes(status)
  );
};

export const isDeactivateIDSAvailable = (idsStatus?: IDSStatus | null) => {
  const activeIdsStatus = [
    'PRODUCTION',
    'Active',
    'ACTIVE',
    'CONNECTION_SUCCESS'
  ];
  return idsStatus && activeIdsStatus.includes(idsStatus);
};

export const isReactivateIDSAvailable = (idsStatus?: IDSStatus | null) => {
  return idsStatus && idsStatus === 'DEACTIVATED';
};

export const isIDSActivationInTransition = (idsStatus?: IDSStatus | null) => {
  const _inTransitionStatuses = [
    'ENROLL_INITIATED',
    'DEACTIVATION_INITIATED',
    'Deactivation_Initiated',
    'REACTIVATION_INITIATED'
  ];
  return !idsStatus || (idsStatus && _inTransitionStatuses.includes(idsStatus));
};

export const isIDSConnectionError = (
  idsStatus?: string | null,
  idsStatusDetail?: string | null
) => {
  const _idsErrorStatuses = ['CONNECTION_FAILURE', 'DATA_ACCESS_FAILURE'];
  const _idsErrorStatusDetailNoUpdateOption = [
    'UNSUCCESSFUL_EMAIL_APPROVAL',
    'NO_DATA_AVAILABLE'
  ];
  return (
    idsStatus &&
    _idsErrorStatuses.includes(idsStatus) &&
    (!idsStatusDetail ||
      !_idsErrorStatusDetailNoUpdateOption.includes(idsStatusDetail))
  );
};

export const validateInputfields = (
  Credentials: IUpdateAccount,
  isUpdateAccount: boolean
) => {
  let Valid = true;
  let tempreverify = { ...Credentials };
  const setError = (field: string) => {
    Valid = false;
    return `Please enter ${field}`;
  };
  tempreverify.usernameError = tempreverify.username
    ? ''
    : setError('username');
  tempreverify.passwordError = tempreverify.password
    ? ''
    : setError('password');
  if (isUpdateAccount)
    tempreverify.accountNumberError = tempreverify.accountNumber
      ? ''
      : setError('account number');

  return { Valid, tempreverify };
};

export const getUtilityImageElemement = (utilityType: string) => {
  if (utilityType === 'Water')
    return <img className="card-utility-icon" src={WaterIcon} />;
  else if (utilityType === 'Gas')
    return <img className="card-utility-icon" src={GasIcon} />;
  else if (utilityType === 'Sewer')
    return <img className="sewer-utility-icon" src={SewerageIcon} />;
  else return <img className="card-utility-icon" src={ElectricityIcon} />;
};

export const validateConnecttabInput = (
  allConnectDetails: IAllConnectDetails
) => {
  let tempValidation = { ...allConnectDetails };
  let isValid = true;
  const setError = (field: string) => {
    isValid = false;
    return `Please enter ${field}`;
  };
  tempValidation.usernameError = tempValidation.username
    ? ''
    : setError('username');
  tempValidation.passwordError = tempValidation.password
    ? ''
    : setError('password');
  tempValidation.accountDetails.forEach((account) => {
    if (account.accountNumber) account.accountNumberError = '';
    else account.accountNumberError = setError('account number');
    if (account.utility) {
      account.utilityError = '';
    } else {
      account.utilityError = 'Please select utility type';
      isValid = false;
    }
  });
  return { isValid, tempValidation };
};

export const getEndDateForUtility = (endDate: string, startDate: string) => {
  let fixDateForAllBrowsers = endDate.replace(/-/g, '/');
  let selectedTimeSpan = new Date(fixDateForAllBrowsers);

  if (selectedTimeSpan.getMonth() === new Date().getMonth()) {
    return excludeCurrentMonth(endDate, startDate);
  } else {
    return moment(selectedTimeSpan).format('YYYY-MM-DD');
  }
};

export const excludeCurrentMonth = (date: string, startDate: string) => {
  const StartDate = new Date(startDate);
  let fixDateForAllBrowsers = date.replace(/-/g, '/');
  let selectedTimeSpan = new Date(fixDateForAllBrowsers);
  let expectedMonth = selectedTimeSpan.getMonth();
  if (
    (StartDate.getMonth() === new Date().getMonth() &&
      StartDate.getFullYear() === new Date().getFullYear()) ||
    isSameDay(endOfMonth(selectedTimeSpan), selectedTimeSpan) ||
    isSameMonth(StartDate, selectedTimeSpan)
  ) {
    expectedMonth = expectedMonth + 1;
  }
  let endDateOfMonth = moment(selectedTimeSpan).set({
    year: selectedTimeSpan.getFullYear(),
    month: expectedMonth,
    date: 0,
    hour: 23,
    minute: 59,
    second: 59
  });
  return endDateOfMonth.toDate().toLocaleDateString('en-CA');
};
// Add support for water
export const basicGraphEnergy = (
  monthWiseAggregations: Array<IBasicMonthWise>,
  fromUnit: string,
  originalUnit: string,
  conversionType: string = ConversionType.ELECTRICITY,
  lng?: string
) => {
  const valuesArray: number[] = [];
  monthWiseAggregations.forEach((monthdata) => {
    valuesArray.push(
      ...[monthdata.totalBaselineConsumption, monthdata.totalConsumption]
    );
  });
  const toUnit = getMaxConvertedValueFromArray(
    conversionType,
    fromUnit,
    valuesArray,
    originalUnit
  ).Unit;
  let value = graphValue(monthWiseAggregations, lng);
  let YearIndex: Array<string> = [];
  let MonthIndex: Array<string> = [];
  let SiteWithDataCount: Array<number | null> = [];
  let IsCompleted: Array<boolean> = [];
  let PeriodUpto: Array<number> = [];
  let actual: Array<number | null> = [];
  let previousPeriod: Array<number | null> = [];
  let noDataActual: boolean = false;
  let noDataPrevious: boolean = false;

  monthWiseAggregations.forEach((x: IBasicMonthWise) => {
    if (x.totalConsumption == null || x.totalConsumption == 0) {
      actual.push(0);
      noDataActual = true;
    } else {
      const unitScaledActual = getConvertedValue(
        conversionType,
        originalUnit, //fromUnit,
        x.totalConsumption,
        toUnit
      ).Value;
      actual.push(unitScaledActual);
    }
    if (x.totalBaselineConsumption == null || x.totalBaselineConsumption == 0) {
      previousPeriod.push(0);
      noDataPrevious = true;
    } else {
      const unitScaledPreviousPeriod = getConvertedValue(
        conversionType,
        originalUnit, //fromUnit,
        x.totalBaselineConsumption,
        toUnit
      ).Value;
      previousPeriod.push(unitScaledPreviousPeriod);
    }

    IsCompleted.push(x.isCompleted);
    PeriodUpto.push(x.consumptionProcessedDays);
    SiteWithDataCount.push(x.sitesWithData || null);
    YearIndex = value.yearIndex;
    MonthIndex = value.monthIndex;
  });
  return {
    actual,
    previousPeriod,
    SiteWithDataCount,
    IsCompleted,
    PeriodUpto,
    YearIndex,
    MonthIndex,
    noDataActual,
    noDataPrevious,
    chartUnit: toUnit
  };
};

export const shouldComponentNotUpdateUnlessGraphChange = (
  prevProps: IAny,
  nextProps: IAny
) => {
  const isGraphDataSame =
    JSON.stringify(prevProps.graphData) === JSON.stringify(nextProps.graphData);
  return isGraphDataSame;
};

export const shouldComponentNotUpdateOnDateChange = (
  prevProps: IEnergyManagementProps,
  nextProps: IEnergyManagementProps
) => {
  const prevStartDate = prevProps?.requestContext?.context?.startTime;
  const nextStartDate = nextProps?.requestContext?.context?.startTime;

  const prevEndDate = prevProps?.requestContext?.context?.endTime;
  const nextEndDate = nextProps?.requestContext?.context?.endTime;

  const prevPreferredEuiUnit = prevProps?.emaasContext?.preferredEuiUnit;
  const nextPreferredEuiUnit = nextProps?.emaasContext?.preferredEuiUnit;

  const prevtoggleFeatureNILM2 = prevProps?.emaasContext?.toggleFeatureNILM2;
  const nexttoggleFeatureNILM2 = nextProps?.emaasContext?.toggleFeatureNILM2;

  const prevtimeFormat = prevProps?.requestContext?.context?.timeFormat;
  const nexttimeFormat = nextProps?.requestContext?.context?.timeFormat;

  const previsNILMSubscribedFormat = prevProps?.emaasContext?.isNILMSubscribed;
  const nextisNILMSubscribedFormat = nextProps?.emaasContext?.isNILMSubscribed;

  const prevhasDisaggIntegration =
    prevProps?.emaasContext?.hasDisaggIntegration;
  const nexthasDisaggIntegration =
    nextProps?.emaasContext?.hasDisaggIntegration;

  const prevshowsDisaggregation = prevProps?.emaasContext?.showsDisaggregation;
  const nextshowsDisaggregation = nextProps?.emaasContext?.showsDisaggregation;

  const prevshowsmeterKPICEMSToggle =
    prevProps?.emaasContext?.meterKPICEMSToggle;
  const nextshowsmeterKPICEMSToggle =
    nextProps?.emaasContext?.meterKPICEMSToggle;

  const prevBillBreakdown =
    prevProps?.emaasContext?.toggleUtilityBreakdownVisualization;
  const nextBillBreakdown =
    nextProps?.emaasContext?.toggleUtilityBreakdownVisualization;

  const isBillBreakdownNotChanged = prevBillBreakdown === nextBillBreakdown;

  const prevtoggleRedaptiveIntegration =
    prevProps?.emaasContext?.toggleRedaptiveIntegration;
  const nexttoggleRedaptiveIntegration =
    nextProps?.emaasContext?.toggleRedaptiveIntegration;

  const prevsubscriptionType =
    prevProps?.requestContext?.context?.subscriptionType;
  const nextsubscriptionType =
    nextProps?.requestContext?.context?.subscriptionType;

  const nextisCashFlow = nextProps?.requestContext?.context?.isCashFlow;
  const previscashFlow = prevProps?.requestContext?.context?.isCashFlow;

  const nextgetAllMeterData = nextProps?.getAllMeterData;
  const prevgetAllMeterData = prevProps?.getAllMeterData;
  const prevMeterKPIStatus = prevProps?.emaasContext?.getMeterKpiFlag;
  const nextMeterKPIStatus = nextProps?.emaasContext?.getMeterKpiFlag;
  const isCashFlowNotChanged = nextisCashFlow === previscashFlow;

  const allMeters = nextgetAllMeterData === prevgetAllMeterData;
  const meterKPIStatusNotChanged = prevMeterKPIStatus === nextMeterKPIStatus;

  const meterKPICEMSToggle =
    nextshowsmeterKPICEMSToggle === prevshowsmeterKPICEMSToggle;

  const startDat =
    prevStartDate === nextStartDate &&
    prevEndDate == nextEndDate &&
    prevPreferredEuiUnit === nextPreferredEuiUnit;

  const checkNILMToggle =
    prevtoggleFeatureNILM2 === nexttoggleFeatureNILM2 &&
    prevtimeFormat === nexttimeFormat &&
    previsNILMSubscribedFormat === nextisNILMSubscribedFormat;

  const checkDisaggToggle =
    prevhasDisaggIntegration == nexthasDisaggIntegration &&
    prevshowsDisaggregation == nextshowsDisaggregation &&
    prevtoggleRedaptiveIntegration == nexttoggleRedaptiveIntegration;

  const prevPointHistoryToggle =
    prevProps?.emaasContext?.pointHistoryUploadToggle;
  const nextPointHistoryToggle =
    nextProps?.emaasContext?.pointHistoryUploadToggle;

  const checkForPointHistoryToggle =
    prevPointHistoryToggle === nextPointHistoryToggle;
  const isUserPreferredUnitnotChanged =
    prevProps?.emaasContext?.userPreferedUnit ===
    nextProps?.emaasContext?.userPreferedUnit;

  const checkToggle = checkNILMToggle && checkDisaggToggle;

  const checkSiteDidNotChange =
    prevProps?.emaasContext?.siteId === nextProps?.emaasContext?.siteId;

  const checkButtnGrpDidNotChange =
    JSON.stringify(prevProps?.emaasContext?.basicAdvancedGrp) ===
    JSON.stringify(nextProps?.emaasContext?.basicAdvancedGrp);
  const checkAdvancedGrpDidNotChange =
    JSON.stringify(prevProps?.emaasContext?.monthlyBillingGrp) ===
    JSON.stringify(nextProps?.emaasContext?.monthlyBillingGrp);
  const checkisOverviewDidNotChange =
    prevProps?.emaasContext?.overViewElectricity ===
    nextProps?.emaasContext?.overViewElectricity;
  const checkIsMonthlyDidNotChange =
    prevProps?.emaasContext?.isMonthlyElectricity ===
    nextProps?.emaasContext?.isMonthlyElectricity;

  const checkWaterTabFlagDidNotChange =
    prevProps?.emaasContext?.enableWaterUsageDashboards ===
    nextProps?.emaasContext?.enableWaterUsageDashboards;
  const checkEOTabListDidNotChange =
    JSON.stringify(prevProps?.emaasContext?.eoAvailableTabs) ===
    JSON.stringify(nextProps?.emaasContext?.eoAvailableTabs);

  const eoTriggerDateChangeChanged =
    prevProps?.emaasContext?.triggerDateChange ===
    nextProps?.emaasContext?.triggerDateChange;
  const onToggleSeparateAPI =
    prevProps?.emaasContext?.toggleSeparateApi ===
    nextProps?.emaasContext?.toggleSeparateApi;

  const didDataSourceNotChange =
    nextProps?.requestContext?.context?.dataSource ===
    prevProps?.requestContext?.context?.dataSource;

  const hasEoStatusNotChanged =
    prevProps?.emaasContext?.eoStatus === nextProps?.emaasContext?.eoStatus;

  const hasEoStatusRuntimeNotChanged =
    prevProps?.emaasContext?.eoStatusRuntime?.values?.length ===
    nextProps?.emaasContext?.eoStatusRuntime?.values?.length;

  const hasEoActiveHoursNotChanged =
    prevProps?.emaasContext?.eoActiveHours ===
    nextProps?.emaasContext?.eoActiveHours;

  const hasEoOperationaltimeNotChanged =
    prevProps?.emaasContext?.eoOperationalTime ===
    nextProps?.emaasContext?.eoOperationalTime;

  const hasReasonForNotChanged =
    prevProps?.emaasContext?.reasonForOff?.length ===
    nextProps?.emaasContext?.reasonForOff?.length;

  if (
    startDat &&
    checkToggle &&
    prevsubscriptionType === nextsubscriptionType &&
    isCashFlowNotChanged &&
    isBillBreakdownNotChanged &&
    allMeters &&
    checkForPointHistoryToggle &&
    isUserPreferredUnitnotChanged &&
    checkSiteDidNotChange &&
    meterKPICEMSToggle &&
    checkButtnGrpDidNotChange &&
    checkAdvancedGrpDidNotChange &&
    checkisOverviewDidNotChange &&
    checkIsMonthlyDidNotChange &&
    checkWaterTabFlagDidNotChange &&
    checkEOTabListDidNotChange &&
    eoTriggerDateChangeChanged &&
    onToggleSeparateAPI &&
    didDataSourceNotChange &&
    hasEoStatusNotChanged &&
    hasEoStatusRuntimeNotChanged &&
    meterKPIStatusNotChanged &&
    hasEoOperationaltimeNotChanged &&
    hasEoActiveHoursNotChanged &&
    hasReasonForNotChanged
  ) {
    return true;
  } else {
    return false;
  }
};

export const shouldComponentNotUpdate = (
  prevProps: IEnergyManagementProps,
  nextProps: IEnergyManagementProps
) => {
  const prevStartDate = prevProps?.requestContext?.context?.startTime;
  const nextStartDate = nextProps?.requestContext?.context?.startTime;

  const prevEndDate = prevProps?.requestContext?.context?.endTime;
  const nextEndDate = nextProps?.requestContext?.context?.endTime;

  const prevPreferredEuiUnit = prevProps?.emaasContext?.preferredEuiUnit;
  const nextPreferredEuiUnit = nextProps?.emaasContext?.preferredEuiUnit;

  const prevtoggleFeatureNILM2 = prevProps?.emaasContext?.toggleFeatureNILM2;
  const nexttoggleFeatureNILM2 = nextProps?.emaasContext?.toggleFeatureNILM2;

  const prevtimeFormat = prevProps?.requestContext?.context?.timeFormat;
  const nexttimeFormat = nextProps?.requestContext?.context?.timeFormat;

  const previsNILMSubscribedFormat = prevProps?.emaasContext?.isNILMSubscribed;
  const nextisNILMSubscribedFormat = nextProps?.emaasContext?.isNILMSubscribed;

  const prevhasDisaggIntegration =
    prevProps?.emaasContext?.hasDisaggIntegration;
  const nexthasDisaggIntegration =
    nextProps?.emaasContext?.hasDisaggIntegration;

  const prevshowsDisaggregation = prevProps?.emaasContext?.showsDisaggregation;
  const nextshowsDisaggregation = nextProps?.emaasContext?.showsDisaggregation;

  const prevshowsmeterKPICEMSToggle =
    prevProps?.emaasContext?.meterKPICEMSToggle;
  const nextshowsmeterKPICEMSToggle =
    nextProps?.emaasContext?.meterKPICEMSToggle;

  const prevBillBreakdown =
    prevProps?.emaasContext?.toggleUtilityBreakdownVisualization;
  const nextBillBreakdown =
    nextProps?.emaasContext?.toggleUtilityBreakdownVisualization;

  const isBillBreakdownNotChanged = prevBillBreakdown === nextBillBreakdown;

  const prevtoggleRedaptiveIntegration =
    prevProps?.emaasContext?.toggleRedaptiveIntegration;
  const nexttoggleRedaptiveIntegration =
    nextProps?.emaasContext?.toggleRedaptiveIntegration;

  const prevsubscriptionType =
    prevProps?.requestContext?.context?.subscriptionType;
  const nextsubscriptionType =
    nextProps?.requestContext?.context?.subscriptionType;

  const nextisCashFlow = nextProps?.requestContext?.context?.isCashFlow;
  const previscashFlow = prevProps?.requestContext?.context?.isCashFlow;

  const nextgetAllMeterData = nextProps?.getAllMeterData;
  const prevgetAllMeterData = prevProps?.getAllMeterData;
  const prevMeterKPIStatus = prevProps?.emaasContext?.getMeterKpiFlag;
  const nextMeterKPIStatus = nextProps?.emaasContext?.getMeterKpiFlag;
  const isCashFlowNotChanged = nextisCashFlow === previscashFlow;

  const allMeters = nextgetAllMeterData === prevgetAllMeterData;
  const meterKPIStatusNotChanged = prevMeterKPIStatus === nextMeterKPIStatus;

  const meterKPICEMSToggle =
    nextshowsmeterKPICEMSToggle === prevshowsmeterKPICEMSToggle;

  const startDat =
    prevStartDate === nextStartDate &&
    prevEndDate == nextEndDate &&
    prevPreferredEuiUnit === nextPreferredEuiUnit;

  const checkNILMToggle =
    prevtoggleFeatureNILM2 === nexttoggleFeatureNILM2 &&
    prevtimeFormat === nexttimeFormat &&
    previsNILMSubscribedFormat === nextisNILMSubscribedFormat;

  const checkDisaggToggle =
    prevhasDisaggIntegration == nexthasDisaggIntegration &&
    prevshowsDisaggregation == nextshowsDisaggregation &&
    prevtoggleRedaptiveIntegration == nexttoggleRedaptiveIntegration;

  const prevPointHistoryToggle =
    prevProps?.emaasContext?.pointHistoryUploadToggle;
  const nextPointHistoryToggle =
    nextProps?.emaasContext?.pointHistoryUploadToggle;

  const checkForPointHistoryToggle =
    prevPointHistoryToggle === nextPointHistoryToggle;
  const isUserPreferredUnitnotChanged =
    prevProps?.emaasContext?.userPreferedUnit ===
    nextProps?.emaasContext?.userPreferedUnit;

  const checkToggle = checkNILMToggle && checkDisaggToggle;

  const checkSiteDidNotChange =
    prevProps?.emaasContext?.siteId === nextProps?.emaasContext?.siteId;

  const checkButtnGrpDidNotChange =
    JSON.stringify(prevProps?.emaasContext?.basicAdvancedGrp) ===
    JSON.stringify(nextProps?.emaasContext?.basicAdvancedGrp);
  const checkAdvancedGrpDidNotChange =
    JSON.stringify(prevProps?.emaasContext?.monthlyBillingGrp) ===
    JSON.stringify(nextProps?.emaasContext?.monthlyBillingGrp);
  const checkisOverviewDidNotChange =
    prevProps?.emaasContext?.overViewElectricity ===
    nextProps?.emaasContext?.overViewElectricity;
  const checkIsMonthlyDidNotChange =
    prevProps?.emaasContext?.isMonthlyElectricity ===
    nextProps?.emaasContext?.isMonthlyElectricity;

  const checkWaterTabFlagDidNotChange =
    prevProps?.emaasContext?.enableWaterUsageDashboards ===
    nextProps?.emaasContext?.enableWaterUsageDashboards;
  const checkEOTabListDidNotChange =
    JSON.stringify(prevProps?.emaasContext?.eoAvailableTabs) ===
    JSON.stringify(nextProps?.emaasContext?.eoAvailableTabs);

  const eoTriggerDateChangeChanged =
    prevProps?.emaasContext?.triggerDateChange ===
    nextProps?.emaasContext?.triggerDateChange;
  const onToggleSeparateAPI =
    prevProps?.emaasContext?.toggleSeparateApi ===
    nextProps?.emaasContext?.toggleSeparateApi;

  const didDataSourceNotChange =
    nextProps?.requestContext?.context?.dataSource ===
    prevProps?.requestContext?.context?.dataSource;

  const hasDataSourceNotChanged =
    prevProps?.dataSourceType === nextProps?.dataSourceType;

  if (
    startDat &&
    checkToggle &&
    prevsubscriptionType === nextsubscriptionType &&
    isCashFlowNotChanged &&
    isBillBreakdownNotChanged &&
    allMeters &&
    checkForPointHistoryToggle &&
    isUserPreferredUnitnotChanged &&
    checkSiteDidNotChange &&
    meterKPICEMSToggle &&
    checkButtnGrpDidNotChange &&
    checkAdvancedGrpDidNotChange &&
    checkisOverviewDidNotChange &&
    checkIsMonthlyDidNotChange &&
    checkWaterTabFlagDidNotChange &&
    checkEOTabListDidNotChange &&
    eoTriggerDateChangeChanged &&
    onToggleSeparateAPI &&
    didDataSourceNotChange &&
    meterKPIStatusNotChanged &&
    hasDataSourceNotChanged
  ) {
    return true;
  } else {
    return false;
  }
};

export const convertDataForUtilityCostSavingsMeter = (
  utilityMeters: IPortfolioUtilityCost[],
  fromUnit: string,
  toUnit: string,
  conversionType: string,
  showChangeWhenData: boolean = false
) => {
  let meterSavingsData: IUtilityCostSavings[] = [];

  utilityMeters.forEach((data) => {
    const { siteId, utilityType, aggregations, siteName } = data;
    const {
      totalActualConsumption,
      totalActualCost,
      previousPeriodConsumption,
      previousPeriodCost,
      totalCostSavings,
      totalCostSavingsPercentage,
      totalConsumptionSavings,
      totalConsumptionSavingsPercentage
    } = aggregations;
    const hasBaseline =
      showChangeWhenData &&
      (isNaN(Number(totalActualConsumption)) ||
        isNaN(Number(previousPeriodConsumption)) ||
        Number(totalActualConsumption) === 0 ||
        Number(previousPeriodConsumption) === 0 ||
        Math.abs(Number(totalConsumptionSavingsPercentage)) >
          PERCENT_CHANGE_THRESHOLD);
    const savingPercent =
      showChangeWhenData &&
      (isNaN(Number(totalActualCost)) ||
        isNaN(Number(previousPeriodCost)) ||
        Number(totalActualCost) === 0 ||
        Number(previousPeriodCost) === 0 ||
        Math.abs(Number(totalCostSavingsPercentage)) >
          PERCENT_CHANGE_THRESHOLD);

    let meterSavings: IUtilityCostSavings = {
      siteId,
      siteName,
      totalActualConsumption: totalActualConsumption,
      actualUnit: getMaxConvertedValueFromArray(
        conversionType,
        fromUnit,
        [totalActualConsumption],
        fromUnit
      ).Unit,
      totalActualCost,
      previousPeriodConsumption: previousPeriodConsumption,
      baselineUnit: getMaxConvertedValueFromArray(
        conversionType,
        fromUnit,
        [previousPeriodConsumption],
        fromUnit
      ).Unit,
      previousPeriodCost,
      totalCostSavings: savingPercent ? null : totalCostSavings,
      totalConsumptionSavings: hasBaseline ? null : totalConsumptionSavings,

      changeUnit: getMaxConvertedValueFromArray(
        conversionType,
        fromUnit,
        [totalConsumptionSavings],
        fromUnit
      ).Unit,
      percentageCostSavings: totalCostSavingsPercentage,
      percentageConsumptionSaving: totalConsumptionSavingsPercentage,
      utilityType: utilityType
    };
    meterSavingsData.push(meterSavings);
  });
  meterSavingsData = addRelativeCostAndConsupValue(
    meterSavingsData
  ) as IUtilityCostSavings[];
  return meterSavingsData;
};

export const convertDataForUtilityCostSavingsMeterForPortfolio = (
  utilityMeters: IPortfolioUtilityCost[],
  fromUnit: string,
  toUnit: string,
  conversionType: string,
  showChangeWhenData: boolean = false
) => {
  let meterSavingsData: IUtilityCostSavings[] = [];

  utilityMeters.forEach((data) => {
    const { siteId, utilityType, aggregations, siteName, isCompleted } = data;
    const {
      totalActualConsumption,
      totalActualCost,
      previousPeriodConsumption,
      previousPeriodCost,
      totalCostSavings,
      totalCostSavingsPercentage,
      totalConsumptionSavings,
      totalConsumptionSavingsPercentage
    } = aggregations;
    const hasBaseline =
      showChangeWhenData &&
      (isNaN(Number(totalActualConsumption)) ||
        isNaN(Number(previousPeriodConsumption)) ||
        Number(totalActualConsumption) === 0 ||
        Number(previousPeriodConsumption) === 0 ||
        !isCompleted ||
        Math.abs(Number(totalConsumptionSavingsPercentage)) >
          PERCENT_CHANGE_THRESHOLD);
    const savingPercent =
      showChangeWhenData &&
      (isNaN(Number(totalActualCost)) ||
        isNaN(Number(previousPeriodCost)) ||
        Number(totalActualCost) === 0 ||
        Number(previousPeriodCost) === 0 ||
        !isCompleted ||
        Math.abs(Number(totalCostSavingsPercentage)) >
          PERCENT_CHANGE_THRESHOLD);

    let meterSavings: IUtilityCostSavings = {
      siteId,
      siteName,
      totalActualConsumption: totalActualConsumption,
      actualUnit: getMaxConvertedValueFromArray(
        conversionType,
        fromUnit,
        [totalActualConsumption],
        fromUnit
      ).Unit,
      totalActualCost,
      previousPeriodConsumption: previousPeriodConsumption,
      baselineUnit: getMaxConvertedValueFromArray(
        conversionType,
        fromUnit,
        [previousPeriodConsumption],
        fromUnit
      ).Unit,
      previousPeriodCost,
      totalCostSavings: savingPercent ? null : totalCostSavings,
      totalConsumptionSavings: hasBaseline ? null : totalConsumptionSavings,

      changeUnit: getMaxConvertedValueFromArray(
        conversionType,
        fromUnit,
        [totalConsumptionSavings],
        fromUnit
      ).Unit,
      percentageCostSavings: totalCostSavingsPercentage,
      percentageConsumptionSaving: totalConsumptionSavingsPercentage,
      utilityType: utilityType
    };
    meterSavingsData.push(meterSavings);
  });
  meterSavingsData = addRelativeCostAndConsupValue(
    meterSavingsData
  ) as IUtilityCostSavings[];
  return meterSavingsData;
};

export const shouldNotUpdate = () => {
  return true;
};

export const shouldNotUpdateStackedChart = (
  prevProps: IStackedColumnProps,
  currentProps: IStackedColumnProps
) => {
  if (prevProps.chartData !== currentProps.chartData) {
    return false;
  }
  return true;
};

export const getChartTooltipInfo = (
  axisUnitType: string,
  actualValue: number,
  unit: string
) => {
  if (axisUnitType === 'currency') {
    return formatCurrency(actualValue, unit, false);
  } else {
    return `${actualValue} ${unit}`;
  }
};
export const getUtilityImage = (utilityType: string) => {
  switch (utilityType) {
    case UtilityType.Water:
      return <img className="card-electricity-icon" src={WaterIcon} />;
    case UtilityType.Gas:
      return <img className="card-electricity-icon" src={GasIcon} />;
    case UtilityType.Sewerage:
      return <img className="card-electricity-icon" src={SewerageIcon} />;
    case UtilityType.Sewer:
      return <img className="card-electricity-icon" src={SewerageIcon} />;
    default:
      return <img className="card-electricity-icon" src={ElectricityIcon} />;
  }
};
export const getMeterName = (materName: string) => {
  if (materName && materName.length >= MAGIC_VALUE.TWENTY) {
    return (
      <div className="measurementunit">
        <TruncateText text={materName} />
      </div>
    );
  } else return <div>{materName}</div>;
};

export const sizeCheck = (
  actualLength: number,
  unitLength: number,
  savingsLength: number
) => {
  if (actualLength + unitLength + savingsLength >= MAGIC_VALUE.TWENTY) {
    return 'small';
  } else if (actualLength + unitLength + savingsLength >= MAGIC_VALUE.FIFTEEN) {
    return 'medium';
  } else {
    return '';
  }
};

export const savingPercentageCheck = (
  savingsPercentage: number | null,
  savings: number
) => {
  if (savingsPercentage! > 0 || (savingsPercentage === 0 && savings === 0)) {
    return 'green';
  } else {
    return 'red';
  }
};

export const conversionCheck = (sourceType: string) => {
  if (sourceType == 'Gas') {
    return ConversionType.GAS;
  } else if (sourceType == 'Water') {
    return ConversionType.WATER;
  } else {
    return ConversionType.ELECTRICITY;
  }
};

export const maxValBDBSCheck = (
  site: IPortfolioEnergySite,
  actualFieldForProgressbar: consuptionFields,
  baselineFieldForProgressbar: baselineFields
) =>
  site[actualFieldForProgressbar]! > site[baselineFieldForProgressbar]!
    ? site[actualFieldForProgressbar]
    : site[baselineFieldForProgressbar];

export const maxConsupValueCheck = (
  maxVal: number | null,
  maxConsupValueforProgressBar: number | null
) =>
  maxVal! > maxConsupValueforProgressBar!
    ? maxVal
    : maxConsupValueforProgressBar;

export const maxEUIValCheck = (actualEui: number, baselineEui: number) =>
  actualEui > baselineEui ? actualEui : baselineEui;
export const maxEuiValueCheck = (
  maxEUIVal: number,
  maxEuiValueforProgressBar: number
) =>
  maxEUIVal > maxEuiValueforProgressBar ? maxEUIVal : maxEuiValueforProgressBar;

export const handleUnitConversion = (
  value: number | null,
  conversionType: string,
  energyMeasurementUnit: string,
  converToUnit: string
) => {
  if (value === null) return null;
  else {
    return getConvertedValue(
      conversionType,
      energyMeasurementUnit,
      value,
      converToUnit
    ).Value;
  }
};

export const clsNameCheck = (apiCallAt: ECartAt | undefined) => {
  if (apiCallAt === ECartAt.siteLabel) {
    return 'score-category-meterdata-meter';
  } else {
    return 'score-category-meterdata-meter portfolio-navigation-link';
  }
};

export const savingsBadgeStyleCheck = (value: number) =>
  value >= 0 ? '#7eb338' : '#f15a4f';

export const totalConsumptionSavingsCheck = (
  totalConsumptionSavings: number
) => {
  if (totalConsumptionSavings < 0) {
    return <Badge color="red" empty={true} />;
  } else {
    return <span className="dummyBadge" />;
  }
};

export const scoreCategoryPercentCheck = (value: IAny) => {
  if (value && value.length <= MAGIC_VALUE.FIVE) {
    return (
      <div className="measurementunit">
        <TruncateText text={value} />
      </div>
    );
  } else {
    return <div>{value}</div>;
  }
};

export const getValueFromArray = (
  valueArray: (number | null)[] | undefined,
  index: number
) => {
  if (valueArray) {
    return valueArray[index];
  } else {
    return undefined;
  }
};

export const baselineCOnsumptionCondition = (
  category: string | undefined,
  t: TFunction,
  renderedFromCarbon?: boolean
) => {
  const d = descriptors.energyManagement;
  if (category === UtilityType.CO2) {
    return `${t(d.PREVIOUS)} ${t(d.EMISSION)}`;
  } else if (category === EnergyUsageField.ENERGY_USAGE) {
    return t(d.PREVIOUSPERIOD);
  } else {
    return renderedFromCarbon ? t(d.PREVIOUSEMISSIONS) : t(d.PREVIOUS);
  }
};

export const actualAvoidanceCondition = (
  category: string | undefined,
  t: TFunction,
  renderedFromCarbon?: boolean
) => {
  const d = descriptors.energyManagement;
  if (category === UtilityType.CO2) {
    return `${t(d.ACTUALAVOIDANCE)} `;
  } else if (category === EnergyUsageField.ENERGY_USAGE) {
    return t(d.BASELINE);
  } else {
    return renderedFromCarbon ? t(d.PREVIOUSEMISSIONS) : t(d.PREVIOUSPERIOD);
  }
};

export const previousAvoidanceCondition = (
  category: string | undefined,
  t: TFunction,
  renderedFromCarbon?: boolean
) => {
  const d = descriptors.energyManagement;
  if (category === UtilityType.CO2) {
    return `${t(d.PREVIOUSAVOIDANCE)} `;
  } else if (category === EnergyUsageField.ENERGY_USAGE) {
    return t(d.BASELINE);
  } else {
    return renderedFromCarbon ? t(d.PREVIOUSEMISSIONS) : t(d.PREVIOUSPERIOD);
  }
};

export const baselineCostDataDisplay = (
  baselineCostData: number[] | undefined,
  measurementUnit: string,
  baselineConsumptionData: (number | null)[] | undefined,
  t: TFunction
) => {
  const d = descriptors.energyManagement;
  if (baselineCostData) {
    return (
      <Chart.Column
        showDataLabels={false}
        name={`${measurementUnit} ${d.BASELINE.defaultMessage}`}
        data={baselineCostData}
        yAxis={0}
        index={undefined}
        id={'0'}
      />
    );
  } else {
    return (
      <Chart.Column
        showDataLabels={false}
        name={`${measurementUnit} ${d.PREVIOUSPERIOD.defaultMessage}`}
        data={baselineConsumptionData}
        yAxis={0}
        index={undefined}
        id={'0'}
      />
    );
  }
};
export const actualCostDataDisplay = (
  actualCostData: number[] | undefined,
  measurementUnit: string,
  actualConsumptionData: (number | null)[] | undefined,
  t: TFunction
) => {
  const d = descriptors.energyManagement;
  if (actualCostData) {
    return (
      <Chart.Column
        showDataLabels={false}
        name={`${measurementUnit} ${d.ACTUAL.defaultMessage}`}
        data={actualCostData}
        yAxis={0}
        index={undefined}
        id={'1'}
      />
    );
  } else {
    return (
      <Chart.Column
        showDataLabels={false}
        name={`${measurementUnit} ${d.ACTUAL.defaultMessage}`}
        data={actualConsumptionData}
        yAxis={0}
        index={undefined}
        id={'1'}
      />
    );
  }
};
export const previousAvoidanceDataDisplay = (
  previousAvoidanceCostData: number[] | undefined,
  measurementUnit: string,
  previousAvoidanceData: (number | null)[] | undefined,
  t: TFunction
) => {
  const d = descriptors.energyManagement;
  if (previousAvoidanceCostData) {
    return (
      <Chart.Column
        showDataLabels={false}
        name={`${measurementUnit} ${d.PREVIOUSAVOIDANCE.defaultMessage}`}
        data={previousAvoidanceCostData}
        yAxis={0}
        index={undefined}
        id={'2'}
        pointWidth={20}
      />
    );
  } else {
    return (
      <Chart.Column
        showDataLabels={false}
        name={`${measurementUnit} ${d.PREVIOUSAVOIDANCE.defaultMessage}`}
        data={previousAvoidanceData}
        yAxis={0}
        index={undefined}
        id={'2'}
        pointWidth={20}
      />
    );
  }
};
export const actualAvoidanceDataDisplay = (
  actualAvoidanceCostData: number[] | undefined,
  measurementUnit: string,
  actualAvoidanceData: (number | null)[] | undefined,
  t: TFunction
) => {
  const d = descriptors.energyManagement;
  if (actualAvoidanceCostData) {
    return (
      <Chart.Column
        showDataLabels={false}
        name={`${measurementUnit} ${d.ACTUALAVOIDANCE.defaultMessage}`}
        data={actualAvoidanceCostData}
        yAxis={0}
        index={undefined}
        id={'3'}
        pointWidth={20}
      />
    );
  } else {
    return (
      <Chart.Column
        showDataLabels={false}
        name={`${measurementUnit} ${t(d.ACTUALAVOIDANCE.defaultMessage)}`}
        data={actualAvoidanceData}
        yAxis={0}
        index={undefined}
        id={'3'}
        pointWidth={20}
      />
    );
  }
};

export const baselineConsDataDisplay = (
  measurementUnit: string,
  baselineConsumptionData: (number | null)[] | undefined,
  t: TFunction
) => {
  const d = descriptors.energyManagement;
  if (baselineConsumptionData) {
    return (
      <Chart.Column
        showDataLabels={false}
        name={`${measurementUnit} ${d.BASELINE.defaultMessage}`}
        data={baselineConsumptionData}
        yAxis={0}
        index={undefined}
        id={'0'}
      />
    );
  } else {
    return undefined;
  }
};

export const actualConsDataDisplay = (
  measurementUnit: string,
  actualConsumptionData: (number | null)[] | undefined,
  t: TFunction
) => {
  const d = descriptors.energyManagement;
  if (actualConsumptionData) {
    return (
      <Chart.Column
        showDataLabels={false}
        name={`${measurementUnit} ${d.ACTUAL.defaultMessage}`}
        data={actualConsumptionData}
        yAxis={0}
        index={undefined}
        id={'1'}
      />
    );
  } else {
    return undefined;
  }
};

export const actualValueCheck = (
  actualConsumptionData: (number | null)[] | undefined,
  x: number
) => (actualConsumptionData ? actualConsumptionData[x] : undefined);
export const baselineValueCheck = (
  baselineConsumptionData: (number | null)[] | undefined,
  x: number
) => (baselineConsumptionData ? baselineConsumptionData[x] : undefined);
export const actualCostCheck = (
  actualCostData: number[] | undefined,
  x: number
) => (actualCostData ? actualCostData[x] : undefined);
export const baselineCostCheck = (
  baselineCostData: number[] | undefined,
  x: number
) => (baselineCostData ? baselineCostData[x] : undefined);

// export const partialDateCheck = (isCompleted: boolean, partialD: number) =>
//   !isCompleted ? `(${partialD}days)` : '';
export const partialDateCheck = (
  isCompleted: boolean,
  partialD: number,
  t: TFunction
) => {
  if (window.location.href.endsWith('portfolio-view')) {
    const d = descriptors.energyManagement;
    if (!isCompleted) return t(d.PARTIAL_MONTH);
    else return '';
  } else {
    if (!isCompleted) return `(${partialD}days)`;
    else return '';
  }
};
export const partialDateCheckUtilitycost = (
  isCompleted: boolean,
  partialD: number,
  t: TFunction
) => {
  if (window.location.href.endsWith('portfolio-view')) {
    const d = descriptors.energyManagement;
    if (!isCompleted) return t(d.PARTIAL_MONTH);
    else return '';
  } else {
    if (!isCompleted) return `(${partialD}days)`;
    else return '';
  }
};
export const utilityCostCurrencyCheck = (
  utilityCostCurrency: string | undefined,
  savings: number,
  toFixedValue?: number,
  fromCarbonKPI?: boolean,
  changeUnit?: string,
  originalUnit?: string,
  conversionType?: string
) =>
  utilityCostCurrency
    ? formatCurrency(savings, utilityCostCurrency, true, toFixedValue)
    : fromCarbonKPI
    ? Math.abs(
        Number(
          getConvertedValue(
            conversionType ? conversionType : 'Electricity',
            originalUnit ? originalUnit : 'kilowatt hour',
            savings,
            changeUnit
          )?.Value
        )
      )
    : Math.abs(savings);
export const shouldRenderBaselineCheck = (
  shouldRenderActual: boolean | undefined,
  shouldRenderSavings: boolean | undefined
) => shouldRenderActual && shouldRenderSavings;
export const cssExcludeDayCheck = (
  excludedDayDescripton: string | undefined
) => {
  if (
    excludedDayDescripton &&
    excludedDayDescripton.length > MAGIC_VALUE.SEVENTEEN
  ) {
    return 'excluded-day-width';
  } else {
    return '';
  }
};

export const cssExcludeDayWithToolTipCheck = (
  excludedDayDescripton: string | undefined
) => {
  if (excludedDayDescripton) {
    return ' excluded-day-with-description-tooltip-row';
  } else {
    return '';
  }
};

export const shouldRenderCheck = (
  shouldRenderActual: boolean | undefined,
  currencyName: string,
  actual: IArraySeriesProps,
  xValue: number,
  isConsumption?: boolean
) => {
  if (shouldRenderActual) {
    let numberFormatter = isConsumption
      ? _.toString
      : formatters.number.currency(currencyName);
    return numberFormatter(
      actual.data!.find((point) => point[MAGIC_VALUE.ZERO] === xValue)![
        MAGIC_VALUE.ONE
      ]
    );
  } else {
    return config.noDataSymbol;
  }
};

export const shouldRenderBCheck = (
  shouldRenderBaseline: boolean | undefined,
  currencyName: string,
  savings: IArraySeriesProps,
  actual: IArraySeriesProps,
  xValue: number,
  isConsumption?: boolean
) => {
  if (shouldRenderBaseline) {
    let numberFormatter = isConsumption
      ? _.toString
      : formatters.number.currency(currencyName);

    if (isConsumption) {
      const baselineTooltip = Number(
        numberFormatter(
          savings.data!.find((point) => point[MAGIC_VALUE.ZERO] === xValue)![
            MAGIC_VALUE.ONE
          ] +
            actual.data!.find((point) => point[MAGIC_VALUE.ZERO] === xValue)![
              MAGIC_VALUE.ONE
            ]
        )
      );
      return baselineTooltip % 1 === 0
        ? baselineTooltip.toString()
        : baselineTooltip.toFixed(MAGIC_VALUE.TWO).toString();
    } else {
      return numberFormatter(
        savings.data!.find((point) => point[MAGIC_VALUE.ZERO] === xValue)![
          MAGIC_VALUE.ONE
        ] +
          actual.data!.find((point) => point[MAGIC_VALUE.ZERO] === xValue)![
            MAGIC_VALUE.ONE
          ]
      );
    }
  } else {
    return config.noDataSymbol;
  }
};

export const EuiEnergyConsumptionCheck = (
  item: ISiteEuiData,
  euiEnergyConsumption: number | undefined
) => (item && euiEnergyConsumption) || MAGIC_VALUE.ZERO;

export const getUtilityTypeDetails = (data: IAny) => {
  const { data: apiData } = data;
  let apiValue;
  if (apiData !== undefined) {
    apiValue = getAccountCount(apiData);
    if (!apiValue.hasOwnProperty('Sewer')) {
      apiValue.Sewer = MAGIC_VALUE.ZERO;
    }
    if (!apiValue.hasOwnProperty('Electricity')) {
      apiValue.Electricity = MAGIC_VALUE.ZERO;
    }
    if (!apiValue.hasOwnProperty('Water')) {
      apiValue.Water = MAGIC_VALUE.ZERO;
    }
    if (!apiValue.hasOwnProperty('Gas')) {
      apiValue.Gas = MAGIC_VALUE.ZERO;
    }
  } else {
    apiValue = {
      Sewer: MAGIC_VALUE.ZERO,
      Electricity: MAGIC_VALUE.ZERO,
      Water: MAGIC_VALUE.ZERO,
      Gas: MAGIC_VALUE.ZERO
    };
  }
  return apiValue;
};

const accountLimit = parseInt(
  window?.ENV?.EMAAS_ACCOUNT_LIMIT,
  MAGIC_VALUE.TEN
);
const isBelowThreshold = (currentValue: IAny) => currentValue > accountLimit;
export const getUtilityTypeCount = (data: IAny) => {
  const { data: apiData } = data;
  let apiValueCheck;
  if (apiData !== undefined) {
    const checkapiValue = getAccountCount(apiData);
    if (!checkapiValue.hasOwnProperty('Sewer')) {
      checkapiValue.Sewer = MAGIC_VALUE.ZERO;
    }
    if (!checkapiValue.hasOwnProperty('Electricity')) {
      checkapiValue.Electricity = MAGIC_VALUE.ZERO;
    }
    if (!checkapiValue.hasOwnProperty('Water')) {
      checkapiValue.Water = MAGIC_VALUE.ZERO;
    }
    if (!checkapiValue.hasOwnProperty('Gas')) {
      checkapiValue.Gas = MAGIC_VALUE.ZERO;
    }
    apiValueCheck = Object.values(checkapiValue).every(isBelowThreshold);
  } else {
    apiValueCheck = false;
  }
  return apiValueCheck;
};
export const SpecialCharacter = (value: string) => specialChars.test(value);
export const SpecialCharacterAutomaticAcc = (value: string) =>
  specialCharsAutomaticAcc.test(value);

const getAccountCount = (apiData: IAny) =>
  apiData.reduce((accumulator: IAny, value: IAny) => {
    return {
      ...accumulator,
      [value.utilityType]:
        (accumulator[value.utilityType] || MAGIC_VALUE.ZERO) + MAGIC_VALUE.ONE
    };
  }, {});

export const filterdYear = (tableData: IAny, FuelGasType: IAny) => {
  return tableData?.find(
    (filterVal: { fuelName: string }) => filterVal.fuelName === FuelGasType
  )?.year;
};

export const getMonthListForLocale = (locale: string = 'en-US'): string[] => {
  const enMonths = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];
  try {
    if (!locale) return enMonths;
    const monthList = moment()
      .locale(locale)
      .localeData()
      .months();
    return monthList;
  } catch (error) {
    return enMonths;
  }
};

export const getMonthDropDown = (year: string, locale?: string) => {
  const d = new Date();
  const fullYear = d.getFullYear();
  const month = getMonthListForLocale(locale);
  let monthDetails;
  if (fullYear === Number(year)) {
    const getMonth = d.getMonth();
    monthDetails = month
      .map((value) => {
        return { text: `${value}`, value: `${value}` };
      })
      .slice(0, getMonth + 1);
  } else {
    monthDetails = month.map((value) => {
      return { text: `${value}`, value: `${value}` };
    });
  }
  return monthDetails;
};

export const unitFilterFun = (
  dropDownData: IAny[],
  value: string,
  category?: any
) => {
  if (category) {
    let displayBasedOnFuelName = dropDownData.filter((addFuleDDValue) => {
      return addFuleDDValue.fuelType.fuelTypeName === value;
    });
    let checkForUpdatedSiteIDWithDetails = getupdatedSiteIDWithDetails(
      displayBasedOnFuelName
    );
    let updatedData;
    if (checkForUpdatedSiteIDWithDetails) {
      updatedData = displayBasedOnFuelName.map((listOfUtilityType) => {
        let emissionFactorDetailsObject: any;
        let emissionFactorDetailsObjectCount = listOfUtilityType?.emissionFactor?.filter(
          (val: { siteId: string; effectiveFrom: moment.MomentInput }) =>
            val.siteId !== GuidEmpty
        );
        let allTheObjectAreKWHCheck = emissionFactorDetailsObjectCount?.every(
          (val: { emissionFactorUnitByCo2eMeasurement: string }) =>
            val.emissionFactorUnitByCo2eMeasurement === 'kgCo2e/kwh'
        );
        if (
          emissionFactorDetailsObjectCount?.length > 1 &&
          !allTheObjectAreKWHCheck
        ) {
          emissionFactorDetailsObject = listOfUtilityType?.emissionFactor?.filter(
            (val: {
              emissionFactorUnitByCo2eMeasurement: string;
              siteId: string;
              effectiveFrom: moment.MomentInput;
            }) =>
              val.siteId !== GuidEmpty &&
              val.emissionFactorUnitByCo2eMeasurement !== 'kgCo2e/kwh'
          )[0]?.fuelTypeUnitId;
        } else {
          emissionFactorDetailsObject = listOfUtilityType?.emissionFactor?.filter(
            (val: { siteId: string; effectiveFrom: moment.MomentInput }) =>
              val.siteId !== GuidEmpty
          )[0]?.fuelTypeUnitId;
        }

        let filteredEmissionFactor = listOfUtilityType?.emissionFactor?.filter(
          (filteredVal: any) =>
            filteredVal?.fuelTypeUnitId === emissionFactorDetailsObject
        );
        let fuelTypeFilter = listOfUtilityType?.fuelTypeUnits.filter(
          (filteredValFuelType: { fuelTypeUnitId: any }) =>
            filteredValFuelType.fuelTypeUnitId === emissionFactorDetailsObject
        );
        return {
          fuelType: listOfUtilityType?.fuelType,
          fuelTypeUnits: fuelTypeFilter,
          emissionFactor: filteredEmissionFactor,
          id: listOfUtilityType.id
        };
      });
      return updatedData;
    } else {
      updatedData = dropDownData.filter((addFuleDDValue) => {
        return addFuleDDValue.fuelType.fuelTypeName === value;
      });
    }
    return updatedData;
  } else {
    return dropDownData.filter((addFuleDDValue) => {
      return addFuleDDValue.fuelType.fuelTypeName === value;
    });
  }
};

export const fuelTypeUnitsFilter = (
  dropDownData: IAny[],
  value: string,
  FuelGasType: string
) => {
  let filterFuelType = dropDownData.filter((addFuleDDValue) => {
    return addFuleDDValue.fuelType.fuelTypeName === FuelGasType;
  });
  return filterFuelType[0].fuelTypeUnits.filter(
    (addFuleDDValue: { unit: string }) => {
      return addFuleDDValue.unit === value;
    }
  );
};

export const onlyMetricTon = (unit: IAny, findVal: IAny) => {
  return unit.find(
    (findMatricTon: { text: string }) => findMatricTon.text === findVal
  );
};

export const metricTonFun = (
  activeIndexDataUpload: number | undefined,
  unit: IAny,
  tableData: IAny,
  FuelGasValue: IAny,
  toggleFeatureAllUpload?: boolean,
  toggleFeatureScopeOne?: boolean,
  toggleFeatureGAS?: boolean,
  toggleFeatureScopeTwo?: boolean
) => {
  let categoryName = fuelGasTypeLabelCheck(
    activeIndexDataUpload,
    'stationaryCombustion',
    'refrigerants',
    toggleFeatureAllUpload,
    toggleFeatureScopeOne,
    toggleFeatureScopeTwo,
    'scope2',
    toggleFeatureGAS,
    'GAS'
  );
  if (categoryName === 'refrigerants') {
    return [onlyMetricTon(unit, 'metric ton')];
  } else {
    const filterdVal = tableData.find(
      (filterVal: { fuelName: string }) => filterVal.fuelName === FuelGasValue
    )?.unit;
    return filterdVal === undefined ? unit : [onlyMetricTon(unit, filterdVal)];
  }
};

export const addFuelButtonCheck = (
  allDetails: { details: IAny[] },
  scope2: any
) => {
  if (scope2) {
    return allDetails.details.some((val) => val.year === '');
  } else {
    return allDetails.details.some(
      (val) =>
        val.month === '' ||
        val.year === '' ||
        val.quantity === '' ||
        val.quantity === 'null'
    );
  }
};

export const formatTooltipHeaderUtilityBreakdown = (ref: { x: string }) => {
  const startMonthAndDate = ref.x.split('-')[0];
  const endMonthAndDate = ref.x.split('-')[1];
  const startyear = startMonthAndDate.split(',')[1];
  const endYear = endMonthAndDate.split(',')[1];
  const changePositionOfStartingMonth = startMonthAndDate
    .split(',')[0]
    .split(' ');
  const changePositionOfEndMonth = endMonthAndDate.split(',')[0].split(' ');
  const formattedStartDate = [
    changePositionOfStartingMonth.reverse().join(' ') + `,${startyear}`
  ];
  const formattedEndDate = [
    changePositionOfEndMonth.reverse().join(' ') + `,${endYear}`
  ];
  return '(' + formattedStartDate + '-' + formattedEndDate + ')';
};

export const formatTooltipHeaderUtilityBreakdownMonthly = (ref: {
  x: string;
}) => {
  return `(${ref.x})`;
};
export const addOneDay = (date: string): string => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + 1);
  return newDate.toISOString();
};

export const splitStringWithSpace = (str: String) => {
  const splitString = str.split(/(?=[A-Z])/);
  return splitString
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
